import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useAuth0 } from '@auth0/auth0-react';
import { useIsPremium } from '../contexts/PremiumContext';
import { openCalendly } from '../utils/calendly';
import '../styles/AnalysisPanel.css';
import '../styles/professional.css';
import KonsesjonAssessment from './KonsesjonAssessment';
import { calcAreaRequirement, calcVoltageFit, calcWaterNeeds, getPriceZone } from '../utils/projectCalcs';
import {
  OVERLAP_CATEGORIES, EEA_ONLY_CATEGORIES, OSM_ONLY_CATEGORIES,
  readEea, readOsm, mergePair, fmtMeters, pairBucket, singleBucket,
} from '../utils/industrialMerge';
import { summarizeGrid, summarizeIndustrial, summarizeHazards, statusClass } from '../utils/sectionSummaries';
import SiteVerdict from './SiteVerdict';
import SiteStory   from './SiteStory';
import BottomLine  from './BottomLine';
import UseCaseSuitability from './UseCaseSuitability';
import SummaryView from './SummaryView';
import DataView    from './DataView';
import VerifiedSiteData from './VerifiedSiteData';
import EstimatesSection from './EstimatesSection';
import MissingData from './MissingData';

// Section title row with optional rule-based status pill + 1-line summary
// (Sprint E · Task 6). Pass `summary` only when it's defined for that section.
function SectionTitle({ title, summary }) {
  if (!summary) return <h4 className="fact-section-title">{title}</h4>;
  return (
    <div className="fact-section-title-row">
      <h4 className="fact-section-title">{title}</h4>
      <span className={statusClass(summary.status)}>{summary.status}</span>
      <div className="fact-section-summary">{summary.summary}</div>
    </div>
  );
}

// Map color name (returned by industrialMerge bucket helpers) to the
// existing fact-row CSS class. Keeps color logic in distanceThresholds.
const colorToRowClass = (c) =>
  c === 'red'   ? 'fact-row-danger'
: c === 'amber' ? 'fact-row-warn'
: c === 'green' ? 'fact-row-clear'
: '';

// ─── Zone name lookup (mirrors backend _ZONE_NAMES) — used when API returns null zone_name ──
const ZONE_NAMES_LOOKUP = {
  1100: 'Residential', 1110: 'Apartment buildings', 1120: 'Terraced housing',
  1130: 'Detached housing', 1140: 'Allotment gardens',
  1200: 'Commercial buildings', 1210: 'Retail', 1220: 'Office', 1230: 'Hotel',
  1300: 'Recreation & tourism', 1400: 'Raw material extraction',
  1500: 'Commercial/Industry', 1510: 'Industry', 1520: 'Warehouse', 1530: 'Cargo terminal',
  1600: 'Sports facility', 1700: 'Public services', 1800: 'Holiday buildings',
  2000: 'Transportation', 2100: 'Road', 2200: 'Railway', 2300: 'Airport', 2400: 'Port',
  2500: 'Energy facility', 2510: 'Power generation', 2520: 'Power distribution',
  2600: 'Water supply/sewerage', 2700: 'Technical infrastructure', 2800: 'Mixed use',
  3000: 'Green structure', 3100: 'Nature area', 3200: 'Walking route', 3300: 'Open area', 3400: 'Park',
  4000: 'Defence',
  5000: 'LNF', 5100: 'Special consideration zone', 5200: 'Nature', 5300: 'Outdoor recreation',
  6000: 'Sea & waterways',
  // 3-digit SOSI 4.x (PBL 1985)
  100: 'Development area', 110: 'Residential use', 120: 'Commercial/industrial use',
  130: 'Public use', 140: 'Open area', 150: 'Holiday buildings',
  200: 'Transportation', 210: 'LNF', 211: 'Agriculture', 212: 'Nature', 213: 'Outdoor recreation',
  220: 'Port/sea area', 300: 'Special area', 310: 'Nature conservation', 320: 'Cultural heritage',
};
// Norwegian kommuneplan abbreviation → full name
const ZONE_ABBR_LOOKUP = {
  'NÆ': 'Næringsformål',
  'BSV': 'Bolig, sosial og velferd',
  'LNFR': 'Landbruks-, natur- og friluftsformål samt reindrift',
  'LNF': 'Landbruks-, natur- og friluftsformål',
  'FRI': 'Friluft / friområde',
  'FIN': 'Friluftsliv i natur',
  'BFS': 'Bolig- og fritidsbebyggelse',
  'OF': 'Offentlig og privat tjenesteyting',
  'IND': 'Industri',
  'VEI': 'Samferdselsanlegg og teknisk infrastruktur',
  'HAV': 'Havn / sjøområde',
  'FEST': 'Fritids- og turistformål',
  'SPA': 'Spesialområde',
};

// Temporary demo overrides — remove once zone detection is fixed
// { wmsName: [displayCode, displayName] }
const ZONE_NAME_OVERRIDES = {
  'Fritid og turistformål': ['NÆ', 'Næringseiendom'],
};

function resolveZoneCode(code, name) {
  if (name) return ZONE_NAME_OVERRIDES[name.trim()]?.[0] ?? code;
  return code;
}
function resolveZoneName(code, name) {
  if (name) return ZONE_ABBR_LOOKUP[name.trim().toUpperCase()] || ZONE_NAME_OVERRIDES[name.trim()]?.[1] || name;
  if (!code) return null;
  const abbr = ZONE_ABBR_LOOKUP[String(code).trim().toUpperCase()];
  if (abbr) return abbr;
  const n = parseInt(code, 10);
  if (isNaN(n)) return null;
  return ZONE_NAMES_LOOKUP[n] || ZONE_NAMES_LOOKUP[Math.floor(n / 100) * 100] || null;
}

// ─── Calculation transparency component ──────────────────────────────────────
// Shows retrieved data vs hardcoded assumptions for any calculation block.
// retrieved / hardcoded: array of [label, value, source?] tuples
function CalcTrace({ retrieved = [], hardcoded = [] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="calc-trace">
      <button className="calc-trace-toggle" onClick={() => setOpen(o => !o)}>
        {open ? '▲ hide' : '▼ show'} inputs
      </button>
      {open && (
        <div className="calc-trace-body">
          {retrieved.length > 0 && (
            <div className="calc-trace-section">
              <div className="calc-trace-header calc-retrieved-hdr">Retrieved data</div>
              {retrieved.map(([label, value, source]) => (
                <div key={label} className="calc-trace-row">
                  <span className="calc-trace-label">{label}</span>
                  <span className="calc-trace-value calc-retrieved">{value}</span>
                  {source && <span className="calc-trace-src">{source}</span>}
                </div>
              ))}
            </div>
          )}
          {hardcoded.length > 0 && (
            <div className="calc-trace-section">
              <div className="calc-trace-header calc-hardcoded-hdr">Hardcoded assumptions</div>
              {hardcoded.map(([label, value, note]) => (
                <div key={label} className="calc-trace-row">
                  <span className="calc-trace-label">{label}</span>
                  <span className="calc-trace-value calc-hardcoded">{value}</span>
                  {note && <span className="calc-trace-src">{note}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Grid CAPEX estimate — voltage-aware transformer + line model (mirrors cost_estimate endpoint) ──
// Sources: Statnett/nettselskap connection cost estimates 2024, NVE nettutredning
function gridCapexMnok(voltageKv, distKm) {
  const dist = distKm || 2;
  const kv = voltageKv || 66;
  let transformer, lineRate;
  if (kv < 33)       { transformer = 8;   lineRate = 2.5; }  // distribution 22kV
  else if (kv < 132) { transformer = 35;  lineRate = 8;   }  // regional 66kV
  else               { transformer = 150; lineRate = 20;  }  // transmission 132kV+
  return Math.round(transformer + lineRate * dist);
}

function FreeCoolingBadge({ lat, lon, country, apiBase }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!lat || !lon) return;
    fetch(`${apiBase}/api/v1/sites/free-cooling-days?lat=${lat}&lon=${lon}&country=${country}`)
      .then(r => r.json()).then(setData).catch(() => {});
  }, [lat, lon, country, apiBase]);
  if (!data) return <span style={{ color: '#64748b', fontSize: 12 }}>Loading…</span>;
  return (
    <span style={{ fontSize: 13, fontWeight: 700, color: data.color }}>
      {data.days} days/yr <span style={{ fontSize: 10, fontWeight: 400, color: data.color }}>({data.rating})</span>
    </span>
  );
}

const AnalysisPanel = ({ analysis, onClose, onAreaChange, projectMw: projectMwProp, onProjectMwChange, enriching, defaultMwNote, selectedCountry = 'NO', useCase = 'hydrogen', onBuildablePolygonChange, onLocate = null }) => {
  // Premium-gating for the Excel export button. Free users see it
  // greyed out with a "Premium" tooltip; clicking opens Calendly with
  // their Auth0 email pre-filled rather than just showing a tooltip
  // dead end (Implementation Brief 2026-06-07).
  const { isPremium } = useIsPremium();
  const { user: auth0User } = useAuth0();
  const [activeTab, setActiveTab] = useState('overview');
  // Audit 2026-05-27 (pro Day 3): view-mode toggle. Defaults to
  // Analysis (the full 4-layer header + scan grid + raw drawer that
  // we ship today). Summary is a 1-page executive view; Data is a
  // flat key/value table for technical users.
  const [viewMode, setViewMode] = useState('analysis');
  // Jump-to-field state (audit 2026-06-01). When BottomLine's clickable
  // counters are activated, we switch to the Data tab and pass the
  // target field key down to DataView, which scrolls the row into view
  // and briefly highlights it. Cleared after one render cycle so the
  // highlight doesn't persist forever.
  const [jumpTargetField, setJumpTargetField] = useState(null);
  const handleJumpToField = (fieldKey) => {
    if (!fieldKey) return;
    setViewMode('data');
    setJumpTargetField(fieldKey);
  };
  // Clear jump target after DataView has had a chance to scroll/highlight.
  React.useEffect(() => {
    if (!jumpTargetField) return;
    const t = setTimeout(() => setJumpTargetField(null), 2000);
    return () => clearTimeout(t);
  }, [jumpTargetField]);

  // Project params — local tech + seawater; MW is owned by SiteScreener via prop
  const [projTech, setProjTech] = useState(
    analysis?.area_assessment?.technology || 'PEM'
  );
  const [projSeawater, setProjSeawater] = useState(
    analysis?.water_needs?.has_seawater_access || false
  );
  const [konsesjonData, setKonsesjonData] = useState(null);

  // Stage 3 + 4 of the buildable-area pipeline — on-demand precise
  // grid + cut/fill + grading-cost from /api/v1/sites/buildable-detail.
  // Cleared whenever the user switches to a different site so the
  // button reads "Compute precise" again rather than showing stale
  // numbers from the previous site.
  const [buildableDetail, setBuildableDetail]       = useState(null);
  const [buildableDetailLoading, setBuildableDetailLoading] = useState(false);
  const [buildableDetailError, setBuildableDetailError]   = useState(null);
  const [konsesjonLoading, setKonsesjonLoading] = useState(false);
  const [konsesjonError, setKonsesjonError] = useState(null);
  // AI water-concession assessment (approval probability + showstoppers
  // + recommendation). Re-added 2026-06-04 alongside the raw NVE data —
  // when NVE returns "0 plants / 0 concessions" for a site, the analyst
  // still wants the regulatory framing the AI assessment gives (which
  // doesn't depend on those datasets having hits in range).
  const [konsesjonAiData, setKonsesjonAiData] = useState(null);
  const [konsesjonAiLoading, setKonsesjonAiLoading] = useState(false);
  const [konsesjonAiError, setKonsesjonAiError] = useState(null);
  // DC cooling-water assessment — POSTs to /sites/cooling-water,
  // returns nearest river / cold source + HydAPI flow + per-strategy
  // viability (free-cooling / evaporative / direct cold-water).
  // Separate from the konsesjon section because the questions are
  // genuinely different — hydropower nearby is irrelevant for DC
  // cooling; flow rate + cold-source distance is everything.
  const [coolingWaterData, setCoolingWaterData] = useState(null);
  const [coolingWaterLoading, setCoolingWaterLoading] = useState(false);
  const [coolingWaterError, setCoolingWaterError] = useState(null);

  // Feature 2: CAPEX/OPEX estimator
  const [costData, setCostData] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  // Audit 2026-05-27 (fix 5): expand cost overview by default.
  // The CAPEX/OPEX numbers are key information; collapsing them hid
  // the per-component breakdown one click deeper than needed.
  const [costOpen, setCostOpen] = useState(true);
  const [costFormOpen, setCostFormOpen] = useState(false);
  const [costTech, setCostTech] = useState('PEM');

  // Feature 5: Water assessment
  const [waterData, setWaterData] = useState(null);
  const [waterLoading, setWaterLoading] = useState(false);
  const [waterOpen, setWaterOpen] = useState(false);

  // LCOH calculator state
  const [lcohData, setLcohData] = useState(null);
  const [lcohLoading, setLcohLoading] = useState(false);
  const [lcohExpanded, setLcohExpanded] = useState(false);
  const [lcohParams, setLcohParams] = useState({ technology: 'PEM', product: 'H2_gas' });
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);

  // LCOD calculator state (data center use-case)
  const [lcodData, setLcodData] = useState(null);
  const [lcodLoading, setLcodLoading] = useState(false);
  const [lcodParams, setLcodParams] = useState({ tier: 'Tier III', cooling_type: 'Air', pue: null });

  // Grid feasibility (external API)
  const [gridFeasData, setGridFeasData] = useState(null);
  const [gridQueueData, setGridQueueData] = useState(null);
  const [gridFeasLoading, setGridFeasLoading] = useState(false);
  const [gridFeasError, setGridFeasError] = useState(null);
  const [gridAiData, setGridAiData] = useState(null);
  const [gridAiLoading, setGridAiLoading] = useState(false);
  const [gridAiError, setGridAiError] = useState(null);

  // Concept comparison state
  const [conceptData, setConceptData] = useState(null);
  const [conceptLoading, setConceptLoading] = useState(false);
  const [conceptExpanded, setConceptExpanded] = useState(false);
  const [dataGapsExpanded, setDataGapsExpanded] = useState(true);
  const [selectedConcepts, setSelectedConcepts] = useState(['H2_gas', 'NH3']);

  // Fetch cost estimate when opened
  useEffect(() => {
    if (!costOpen || costData) return;
    fetchCostEstimate();
  }, [costOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch water assessment when opened
  useEffect(() => {
    if (!waterOpen || waterData) return;
    fetchWaterAssessment();
  }, [waterOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const getSiteId = () => {
    return analysis?._site?._zone_result?.zone_id
      || analysis?._site?.id
      || 'unknown';
  };

  const getSiteCoords = () => {
    const loc = analysis?.location || analysis?._site?._zone_result?.location;
    return loc ? { lat: loc.latitude, lon: loc.longitude } : null;
  };

  const fetchCostEstimate = async () => {
    const coords = getSiteCoords();
    if (!coords) return;
    setCostLoading(true);
    try {
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const z = analysis?._site?._zone_result;
      const site = analysis?._site;
      const muni = z?.municipality || site?.municipality_number || site?.municipality || '03';
      const kv = z?.nearest_grid?.voltage_level || 66;
      const gridKm = z?.nearest_grid?.distance_km || 2;
      const loc = analysis?._site?._location_assessment;
      const portKm = loc?.port_dist_km || 20;
      const waterKm = loc?.water_dist_km || 2;
      const rawSlope = loc?.slope_pct ?? site?.slope_pct ?? 5;
      const slope = rawSlope > 0 ? rawSlope : 5;
      const roadKm = loc?.road_distance_km ?? site?.road_distance_km ?? 1.0;
      const params = new URLSearchParams({
        mw: projectMwProp || 100, technology: costTech,
        grid_distance_km: gridKm, voltage_kv: kv,
        road_distance_km: roadKm, port_distance_km: portKm,
        water_distance_km: waterKm, municipality_number: muni,
        terrain_slope_pct: slope,
      });
      const r = await fetch(`${API_BASE}/api/v1/sites/${getSiteId()}/cost-estimate?${params}`);
      if (r.ok) setCostData(await r.json());
    } catch (e) {
      console.warn('cost-estimate failed:', e);
    } finally {
      setCostLoading(false);
    }
  };

  const fetchWaterAssessment = async () => {
    const coords = getSiteCoords();
    if (!coords) return;
    setWaterLoading(true);
    try {
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const loc = analysis?._site?._location_assessment;
      const waterKm = loc?.water_dist_km || 2;
      const site = analysis?._site;
      const salmonFlag = site?.has_salmon ?? false;
      const protectedFlag = site?.in_protected_watershed ?? false;
      const r = await fetch(
        `${API_BASE}/api/v1/sites/${getSiteId()}/water-assessment?lat=${coords.lat}&lon=${coords.lon}&mw=${projectMwProp || 100}&water_distance_km=${waterKm}&salmon_river_within_500m=${salmonFlag}&is_protected_watershed=${protectedFlag}`
      );
      if (r.ok) setWaterData(await r.json());
    } catch (e) {
      console.warn('water-assessment failed:', e);
    } finally {
      setWaterLoading(false);
    }
  };

  // Stage 3 + 4 fetcher — POSTs to /api/v1/sites/buildable-detail and
  // updates buildableDetail state. Idempotent: subsequent clicks hit
  // the Redis cache server-side (30-day TTL) and return in <10 ms.
  // Site must have a geometry and a teig_id; without those the dense
  // grid analysis can't run and we bail with a sensible error.
  const fetchBuildableDetail = useCallback(async () => {
    const site = analysis?._site;
    if (!site) return;
    if (!site.geometry) {
      setBuildableDetailError('No parcel geometry available — cannot run precise analysis.');
      return;
    }
    const parcelId = site.teig_id || site.matrikkelnummer || site.id;
    if (!parcelId) {
      setBuildableDetailError('No parcel id — cannot key the cache.');
      return;
    }
    setBuildableDetailLoading(true);
    setBuildableDetailError(null);
    try {
      const apiBase = import.meta.env.VITE_API_URL || '';
      const resp = await fetch(`${apiBase}/api/v1/sites/buildable-detail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: site.lat,
          lon: site.lon,
          parcel_id: String(parcelId),
          total_area_ha: site.total_area_ha || 0,
          parcel_geometry: site.geometry,
          // 15 % is the realistic upper bound for an industrial /
          // data-centre site without uneconomical earthworks. The
          // screening pipeline uses 35 % ("could be graded if you
          // really wanted to") which is why precise-vs-screening
          // numbers diverged so much; tighter threshold here gives
          // the analyst the actually-developable footprint.
          max_slope_pct: 15,
          project_mw: projectMwProp || null,
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`HTTP ${resp.status}${txt ? `: ${txt.slice(0, 120)}` : ''}`);
      }
      const data = await resp.json();
      setBuildableDetail(data);
    } catch (e) {
      setBuildableDetailError(e.message || String(e));
    } finally {
      setBuildableDetailLoading(false);
    }
  }, [analysis, projectMwProp]);

  // Clear the buildable-detail state whenever the analysis target
  // (site) changes — otherwise the "Compute precise" button would
  // show stale numbers from the previous site after switching.
  // Precise compute is on-demand only (2026-06-04 revert): the
  // panel starts with no polygon, no precise number — the user
  // clicks "Compute precise buildable area" to fetch it.
  useEffect(() => {
    setBuildableDetail(null);
    setBuildableDetailError(null);
    setBuildableDetailLoading(false);
  }, [analysis?._site?.teig_id, analysis?._site?.matrikkelnummer]);

  // Push the buildable polygon up to the parent so the map can render
  // it as a layer. Fires on detail set AND on clear (null/no polygon),
  // so the layer disappears when the user switches sites.
  useEffect(() => {
    if (!onBuildablePolygonChange) return;
    onBuildablePolygonChange(buildableDetail?.buildable_polygon || null);
  }, [buildableDetail, onBuildablePolygonChange]);

  const fetchLcoh = useCallback(() => {
    const site = analysis?._site;
    if (!site) return;
    setLcohLoading(true);
    const z = analysis?._site?._zone_result;
    const loc = analysis?._site?._location_assessment;
    const voltageKv = z?.nearest_grid?.voltage_level || site?.voltage_level_kv || site?.voltage_kv || 33;
    const roadKm = loc?.road_distance_km ?? site?.road_distance_km ?? 1.0;
    const params = new URLSearchParams({
      mw: projectMwProp || 100,
      technology: lcohParams.technology,
      product: lcohParams.product,
      voltage_kv: voltageKv,
      dist_transmission_km: z?.nearest_grid?.distance_km || site.grid_distance_km || 5,
      dist_road_km: roadKm,
      dist_water_km: loc?.water_dist_km || site.water_distance_km || 4,
      municipality_number: z?.municipality || site.municipality_number || site?.municipality || '03',
    });
    const apiBase = import.meta.env.VITE_API_URL || '';
    const siteId = analysis?._site?._zone_result?.zone_id || analysis?._site?.id || 'draft';
    fetch(`${apiBase}/api/v1/sites/${siteId}/lcoh?${params}`)
      .then(r => r.json())
      .then(data => setLcohData(data))
      .catch(() => {})
      .finally(() => setLcohLoading(false));
  }, [analysis, lcohParams, projectMwProp]);

  const fetchConceptComparison = useCallback(() => {
    const site = analysis?._site;
    if (!site) return;
    setConceptLoading(true);
    const z = analysis?._site?._zone_result;
    const loc = analysis?._site?._location_assessment;
    const apiBase = import.meta.env.VITE_API_URL || '';
    const siteId = z?.zone_id || site.id || 'draft';
    const roadKm = loc?.road_distance_km ?? site?.road_distance_km ?? 1.0;
    const muni = z?.municipality || site?.municipality_number || '03';
    const { price_nok_mwh: ppaPrice } = getPriceZone(muni);
    fetch(`${apiBase}/api/v1/sites/${siteId}/concept-comparison`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mw: projectMwProp || 100,
        technology: lcohParams.technology,
        concepts: selectedConcepts,
        dist_transmission_km: z?.nearest_grid?.distance_km || site.grid_distance_km || 5,
        dist_road_km: roadKm,
        dist_water_km: loc?.water_dist_km || site.water_distance_km || 4,
        elec_price_NOK_per_MWh: ppaPrice,
      }),
    })
      .then(r => r.json())
      .then(d => setConceptData(d))
      .catch(() => {})
      .finally(() => setConceptLoading(false));
  }, [analysis, lcohParams, selectedConcepts, projectMwProp]);

  useEffect(() => {
    if (analysis?._site && lcohExpanded) fetchLcoh();
  }, [analysis, lcohExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset LCOH and concept data when the site changes
  useEffect(() => {
    setLcohData(null);
    setConceptData(null);
  }, [analysis?._site?._zone_result?.zone_id, analysis?._site?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of needed area whenever project params change
  useEffect(() => {
    if (onAreaChange) {
      const area = calcAreaRequirement(projectMwProp || 20, projTech);
      onAreaChange(area.totalHa);
    }
  }, [projectMwProp, projTech, onAreaChange]);

  // Safety check - if analysis data is incomplete, show error
  if (!analysis || !analysis.suitability_score) {
    return (
      <div className="analysis-panel">
        <div className="panel-header">
          <h2>Analysis error</h2>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>
        <div className="panel-content">
          <p>Could not load analysis data. Please try again.</p>
        </div>
      </div>
    );
  }

  const exportToCSV = () => {
    const csvData = [
      ['Site Analysis Report'],
      [''],
      ['Location'],
      ['Latitude', analysis.location.latitude],
      ['Longitude', analysis.location.longitude],
      [''],
      ['Suitability'],
      ['Score', analysis.suitability_score],
      [''],
      ['Summary'],
      [analysis.summary],
    ];
    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `site-analysis-${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    const json = JSON.stringify(analysis, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `site-analysis-${Date.now()}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToExcel = () => {
    const s = analysis._site || {};
    const zr = s._zone_result || {};
    const la = s._location_assessment || {};
    const mw = projectMwProp || 20;

    // ── helpers ──────────────────────────────────────────────────────────────
    const n = (v, unit = '') => v != null ? `${typeof v === 'number' ? v : v}${unit ? ' ' + unit : ''}` : '—';
    const yn = (v) => v ? 'Yes' : 'No';
    const pct = (v) => v != null ? `${typeof v === 'number' ? v.toFixed(1) : v}%` : '—';
    const km  = (v) => v != null ? `${typeof v === 'number' ? v.toFixed(1) : v} km` : '—';

    // ── derive the same fields the panel uses ────────────────────────────────
    const _zoneCode   = zr.zone_code ?? s.zoning_info?.zone_code ?? null;
    const _zoneName   = resolveZoneName(_zoneCode, zr.zone_name ?? s.zoning_info?.zone_name ?? null);
    const _muniNo     = zr.municipality ?? s.municipality_number ?? null;
    const _muniName   = zr.municipality_name ?? s.zoning_info?.municipality ?? null;
    const _gridName   = zr.nearest_grid?.name ?? s.nearest_grid?.name ?? '—';
    const _gridKv     = zr.nearest_grid?.voltage_level ?? s.voltage_level_kv ?? null;
    const _gridOp     = zr.nearest_grid?.operator ?? s.nearest_grid?.operator ?? '—';
    const _gridKm     = zr.nearest_grid?.distance_km ?? s.grid_distance_km ?? null;
    const _gridCapex  = gridCapexMnok(_gridKv, _gridKm);
    const { zone: _pz, price_nok_mwh: _ppa } = getPriceZone(_muniNo);
    const _elev       = s.elevation_m ?? la.elevation_m ?? null;
    const _slope      = s.slope_pct ?? la.slope_pct ?? null;
    const _roadCat    = s.road_category ?? null;
    const _roadKm     = s.road_distance_km ?? la.road_distance_km ?? null;
    const _bridgeLim  = s.bridge_weight_limit_tonnes ?? null;
    const _portName   = s.port_name ?? la.port_name ?? null;
    const _portKm     = s.port_distance_km ?? la.port_dist_km ?? null;
    const _portDepth  = s.port_depth_m ?? la.port_depth_m ?? null;
    const _portEst    = s.port_depth_estimated ?? la.port_depth_estimated ?? false;
    const _waterName  = s.river_name ?? s.lake_name ?? la.river_name ?? la.lake_name ?? null;
    const _waterKm    = s.water_distance_km ?? s.water_dist_km ?? la.water_dist_km ?? null;
    const _waterType  = s.water_source_type ?? null;
    const _salmon     = s.has_salmon ?? s.is_salmon_river ?? false;
    const _protWater  = s.in_protected_watershed ?? false;
    const _protected  = s.nearest_protected_distance_km ?? la.protected_dist_km ?? null;
    const _redList    = s.red_listed_species_count ?? 0;
    const _reindeer   = s.in_reindeer_area ?? false;
    const _kulturminner = s.cultural_monument_count ?? (s.har_kulturminne ? 1 : 0);
    const _ninA       = s.nin_type_a ?? false;
    const _ninAName   = s.nin_type_a_name ?? null;
    const _flood      = s.flood_hard_stop ?? false;
    const _landslide  = s.landslide_hard_stop ?? false;
    const _kvikkleire = s.kvikkleire_hard_stop ?? false;
    const _totalHa    = zr.area_ha ?? s.total_parcel_area_ha ?? null;
    const _lcohUsd    = s.lcoh_usd_kg ?? null;
    const _buildCount = s.building_count ?? null;

    const reqKv = mw <= 10 ? 22 : mw <= 60 ? 66 : mw <= 300 ? 132 : mw <= 600 ? 300 : 420;
    const upgradeNeeded = _gridKv && _gridKv < reqKv;
    const upgradeMnok = upgradeNeeded
      ? (s.voltage_upgrade_mnok ?? ({ '22→66': 30, '22→132': 90, '33→66': 30, '33→132': 80, '66→132': 80, '66→300': 150, '66→420': 200, '132→300': 90, '132→420': 140 }[`${_gridKv}→${reqKv}`] ?? 80))
      : null;

    // ── sheet data — each row is [Section, Field, Value, Note] ──────────────
    // Hydrogen vs data-centre sites get different sections: LCOH/Port/H2-water
    // for hydrogen; LCOD/IXP/fiber for DC. Shared sections (zoning, grid,
    // terrain, hazards, environment, water source) render for both.
    const isDc = useCase === 'data_center';
    const rows = [
      // header
      ['SEIDR Site Analysis', '', '', ''],
      ['Generated', new Date().toISOString().slice(0, 16).replace('T', ' '), '', ''],
      ['Site name', _gridName || 'Unnamed site', '', ''],
      [isDc ? 'IT load (MW)' : 'Plant capacity (MW)', mw, '', ''],
      ['', '', '', ''],

      // LCOH summary (hydrogen only)
      ...(isDc ? [] : [
        ['LCOH SUMMARY', '', '', ''],
        ['Indicative LCOH (base)', _lcohUsd != null ? `$${(_lcohUsd * 0.65).toFixed(1)}–${(_lcohUsd * 1.35).toFixed(1)}/kg` : '—', '', '±35% uncertainty'],
        ['LCOH midpoint ($/kg)', _lcohUsd != null ? _lcohUsd.toFixed(2) : '—', '', 'Pre-feasibility estimate'],
        ['LCOH midpoint (NOK/kg)', _lcohUsd != null ? (_lcohUsd * 10.5).toFixed(1) : '—', '', 'At 10.5 NOK/USD'],
        ['Price zone', _pz, '', ''],
        ['PPA electricity price', `${(_ppa / 10).toFixed(0)} øre/kWh`, '', 'Long-term PPA estimate'],
        ['', '', '', ''],
      ]),

      // Data centre summary (DC only) — mirrors the on-screen LCOD card
      ...(isDc ? [
        ['DATA CENTRE SUMMARY', '', '', ''],
        ['IT load (MW)', mw, '', 'Configured plant capacity'],
        ['Price zone', _pz, '', ''],
        ['Electricity price (NOK/MWh)', _ppa, '', 'Long-term PPA estimate'],
        ['Nearest IXP', s.nearest_ixp_name || '—', '', s.nearest_ixp_city || ''],
        ['Distance to IXP (km)', s.nearest_ixp_distance_km != null ? Number(s.nearest_ixp_distance_km.toFixed(1)) : '—', '', selectedCountry === 'FI' ? 'FICIX seed list' : 'IXP enrichment pending for NO'],
        ['Estimated one-way latency (ms)', s.estimated_latency_ms ?? '—', '', 'Distance × 5 µs/km + overhead'],
        ['Fiber diversity', s.fiber_diversity || '—', '', 'Route options to IXP'],
        ['', '', '', ''],
      ] : []),

      // Property & zoning
      ['PROPERTY & ZONING', '', '', ''],
      ['Zone code', _zoneCode ?? '—', '', ''],
      ['Zone name', _zoneName ?? '—', '', ''],
      ['Zone confidence', zr.zone_code_confidence ?? '—', '', ''],
      ['Zone layer', zr.zone_layer ?? '—', '', ''],
      ['Area (ha)', _totalHa != null ? Number(typeof _totalHa === 'number' ? _totalHa.toFixed(1) : _totalHa) : '—', '', ''],
      ['Municipality number', _muniNo ?? '—', '', ''],
      ['Municipality name', _muniName ?? '—', '', ''],
      ['Dispute / encumbrance', yn(s.har_tvist ?? s.tvist ?? false), '', ''],
      ['Cultural heritage', _kulturminner > 0 ? `${_kulturminner} registered` : 'None', '', 'Riksantikvaren'],
      ['Existing buildings (OSM)', _buildCount != null ? _buildCount : '—', '', 'OpenStreetMap Overpass'],
      ['', '', '', ''],

      // Grid connection
      ['GRID CONNECTION', '', '', ''],
      ['Nearest substation', _gridName, '', ''],
      ['Operator', _gridOp, '', ''],
      ['Voltage level (kV)', _gridKv ?? '—', '', ''],
      ['Distance to substation (km)', _gridKm != null ? Number(typeof _gridKm === 'number' ? _gridKm.toFixed(2) : _gridKm) : '—', '', 'From teig centroid'],
      ['Grid CAPEX estimate (MNOK)', _gridCapex ?? '—', '', 'Transformer + line, hardcoded rates'],
      ['Voltage upgrade required', upgradeNeeded ? `${_gridKv} kV → ${reqKv} kV` : 'No', '', `${mw} MW project`],
      ['Voltage upgrade cost (MNOK)', upgradeMnok ?? '—', '', selectedCountry === 'FI' ? 'Indicative, Fingrid estimate' : 'Indicative, Statnett estimate'],
      ['', '', '', ''],

      // Terrain & access
      ['TERRAIN & ACCESS', '', '', ''],
      ['Elevation (m a.s.l.)', _elev ?? '—', '', selectedCountry === 'FI' ? 'MML / NLS terrain' : 'Kartverket høydedata'],
      ['Max slope (%)', _slope != null ? Number(typeof _slope === 'number' ? _slope.toFixed(1) : _slope) : '—', '', selectedCountry === 'FI' ? 'MML / NLS terrain' : 'Kartverket høydedata'],
      ['Nearest road category', _roadCat ?? '—', '', selectedCountry === 'FI' ? 'Digiroad' : 'NVDB'],
      ['Road distance (km)', _roadKm != null ? Number(typeof _roadKm === 'number' ? _roadKm.toFixed(2) : _roadKm) : '—', '', selectedCountry === 'FI' ? 'Digiroad' : 'NVDB'],
      ['Bridge weight limit (t)', _bridgeLim != null && _bridgeLim > 0 ? _bridgeLim : 'None within 10 km', '', ''],
      ['Construction season (months)', s.construction_season_months ?? '—', '', 'Frost-free months/yr'],
      ['', '', '', ''],

      // Hazard zones
      ['HAZARD ZONES', '', '', ''],
      ['Flood zone (1000-yr)', _flood ? 'EXCLUDED — flood zone' : 'Clear', '', selectedCountry === 'FI' ? 'SYKE flood zones' : 'NVE ArcGIS'],
      ['Landslide hazard', _landslide ? 'EXCLUDED — landslide zone' : 'Clear', '', selectedCountry === 'FI' ? 'SYKE / GTK' : 'NVE ArcGIS'],
      ['Quick clay', _kvikkleire ? '⚠ Quick clay risk — engineering required' : '✓ None detected — stable foundation', '', selectedCountry === 'FI' ? 'N/A (Finland)' : 'NVE ArcGIS'],
      ['Salmon river (500 m)', _salmon ? 'EXCLUDED — salmon river within 500 m' : 'Clear', '', selectedCountry === 'FI' ? 'N/A (Finland)' : 'NVE Lakseregisteret'],
      ['NiN Type A nature', _ninA ? `EXCLUDED — ${_ninAName || 'type A within 200 m'}` : 'Clear', '', selectedCountry === 'FI' ? 'N/A (Finland)' : 'Miljødirektoratet'],
      ['', '', '', ''],

      // Environment
      ['ENVIRONMENT & HERITAGE', '', '', ''],
      ['Red-listed species', _redList > 0 ? `${_redList} records` : 'None found', '', selectedCountry === 'FI' ? 'Laji.fi (FinBIF)' : 'GBIF last 5 years'],
      ['Protected area distance (km)', _protected != null ? Number(typeof _protected === 'number' ? _protected.toFixed(1) : _protected) : '—', '', 'To nearest protected area'],
      ['Reindeer herding area', yn(_reindeer), '', ''],
      ['NiN nature type (B/C)', s.nin_type ?? (_ninA ? 'A' : 'None'), '', 'Within 500 m'],
      ['', '', '', ''],

      // Water
      ['WATER', '', '', ''],
      ['Nearest water source', _waterName ?? 'Not identified', '', selectedCountry === 'FI' ? 'Overpass / SYKE' : 'NVE Elvenett / Innsjødatabase'],
      ['Water distance (km)', _waterKm != null ? Number(typeof _waterKm === 'number' ? _waterKm.toFixed(2) : _waterKm) : '—', '', ''],
      ['Water source type', _waterType === 'saltwater' ? 'Saltwater' : (_waterType ? 'Freshwater' : '—'), '', ''],
      ['Water pipe CAPEX (MNOK)', _waterKm != null ? Math.round((typeof _waterKm === 'number' ? _waterKm : parseFloat(_waterKm) || 0) * 3.5) : '—', '', '3.5 MNOK/km estimate'],
      ['Salmon river', yn(_salmon), '', 'Hard stop if yes'],
      ['Protected watershed', yn(_protWater), '', 'NVE Verneplan vassdrag'],
      // Hydrogen water demand math (electrolysis stoichiometry — not DC)
      ...(isDc ? [] : [
        ['Water demand — freshwater (m³/hr)', Number((800 * mw / 1000).toFixed(1)), '', `${mw} MW × 800 l/hr/MW`],
        ['Water demand — seawater RO (m³/hr)', Number((200 * mw / 1000).toFixed(1)), '', `${mw} MW × 200 l/hr/MW`],
      ]),
      ['', '', '', ''],

      // Port & export — hydrogen-only (DC sites don't ship physical product)
      ...(isDc ? [] : [
        ['PORT & EXPORT', '', '', ''],
        ['Nearest port', _portName ?? '—', '', ''],
        ['Port distance (km)', _portKm != null ? Number(typeof _portKm === 'number' ? _portKm.toFixed(1) : _portKm) : '—', '', ''],
        ['Port depth (m)', _portDepth != null ? `${_portDepth}${_portEst ? ' (estimated)' : ''}` : '—', '', _portEst ? 'Estimated from port type' : 'Measured'],
        ['Suitable for', _portDepth == null ? 'Manual assessment' : _portDepth >= 12 ? 'NH₃ / LH₂ / MeOH' : _portDepth >= 7 ? 'NH₃ / gas-H₂' : _portDepth >= 5 ? 'Gas-H₂ (small tonnage)' : 'Manual assessment', '', ''],
        ['', '', '', ''],
      ]),

      // LCOH model inputs (hydrogen only — DC uses LCOD via the on-screen LCOD card)
      ...(isDc ? [] : [
        ['LCOH MODEL INPUTS', '', '', ''],
        ['Technology', 'PEM', '', ''],
        ['Efficiency (kWh/kg H₂)', 52, '', 'System level incl. BoP'],
        ['Capacity factor', '95%', '', ''],
        ['Plant lifetime (years)', 25, '', ''],
        ['Discount rate (WACC)', '7%', '', ''],
        ['FX rate (NOK/USD)', 10.5, '', ''],
        ['Grid distance input (km)', _gridKm != null ? Number(typeof _gridKm === 'number' ? _gridKm.toFixed(2) : _gridKm) : '3.0 (default)', '', 'Real from KNN query'],
        ['Road distance input (km)', _roadKm != null ? Number(typeof _roadKm === 'number' ? _roadKm.toFixed(2) : _roadKm) : '1.0 (default)', '', 'Real if road filter active, else assumed'],
        ['Water distance input (km)', _waterKm != null ? Number(typeof _waterKm === 'number' ? _waterKm.toFixed(2) : _waterKm) : '2.0 (default)', '', 'Real from KNN query'],
        ['Transmission rate (MNOK/km)', 4.0, '', 'Hardcoded — all-in overhead line'],
        ['Road rate (MNOK/km)', 20.0, '', 'Hardcoded — Erlend estimate'],
        ['Water pipe rate (MNOK/km)', 10.0, '', 'Hardcoded — Erlend estimate'],
        ['', '', '', ''],
      ]),

      // Assumptions disclaimer
      ['DISCLAIMER', '', '', ''],
      ['Accuracy', '±30–35%', '', 'Pre-feasibility estimate only'],
      ['Source', 'LOCP_calculator.xlsx — Jan E / Erlend, 2024', '', ''],
      ['Not suitable for', 'Investment decisions without independent verification', '', ''],
    ];

    // ── build worksheet ──────────────────────────────────────────────────────
    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Column widths
    ws['!cols'] = [
      { wch: 22 },  // Section
      { wch: 36 },  // Field
      { wch: 30 },  // Value
      { wch: 42 },  // Note
    ];

    // Style section header rows (bold, filled) — SheetJS CE doesn't support rich styles,
    // but we can mark them for users who open in Excel with conditional formatting
    // by capitalising and using a leading marker row pattern (standard approach).
    const SECTION_ROWS = rows.reduce((acc, row, i) => {
      if (row[0] && row[0] === row[0].toUpperCase() && row[0].trim() !== '' && row[1] === '') acc.push(i);
      return acc;
    }, []);

    // Freeze top row
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Site Analysis');

    // Add a second sheet with raw JSON for traceability
    const rawRows = [['Field', 'Value']];
    const flatten = (obj, prefix = '') => {
      for (const [k, v] of Object.entries(obj || {})) {
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
          flatten(v, prefix ? `${prefix}.${k}` : k);
        } else {
          rawRows.push([prefix ? `${prefix}.${k}` : k, Array.isArray(v) ? JSON.stringify(v) : String(v ?? '')]);
        }
      }
    };
    flatten(analysis._site || {});
    const wsRaw = XLSX.utils.aoa_to_sheet(rawRows);
    wsRaw['!cols'] = [{ wch: 45 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsRaw, 'Raw Data');

    const siteName = (_gridName || 'site').replace(/[^a-zA-Z0-9æøåÆØÅ\s-]/g, '').trim().slice(0, 30);
    XLSX.writeFile(wb, `SEIDR_${siteName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const analyzeKonsesjon = async () => {
    setKonsesjonLoading(true);
    setKonsesjonError(null);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '';
      // Switched 2026-06-04 from /analyze-konsesjon (LLM, returned
      // approval-probability / showstoppers / etc.) to /water-concessions
      // (raw NVE data, returns actual hydropower plants + their owners +
      // concession entries + verneplan status). Users wanted the actual
      // concessions / dates / companies, not the AI summary.
      // Broader fallback chain — search-result parcels store coords in
      // _zone_result.location, _zone_result.nearest_grid, or as raw
      // lat/lon on _site (see SiteScreener.jsx:1626/1688). Earlier
      // 3-key chain missed those paths, producing lat=undefined →
      // backend 400 → opaque "upstream unavailable" message.
      const s = analysis._site || {};
      const lat = analysis.location?.latitude
        ?? s.location?.latitude
        ?? s._zone_result?.location?.latitude
        ?? s._zone_result?.nearest_grid?.lat
        ?? s.lat
        ?? s.latitude
        ?? null;
      const lng = analysis.location?.longitude
        ?? s.location?.longitude
        ?? s._zone_result?.location?.longitude
        ?? s._zone_result?.nearest_grid?.lon
        ?? s.lon
        ?? s.longitude
        ?? null;
      if (lat == null || lng == null) {
        throw new Error('No coordinates available for this site');
      }
      const response = await fetch(`${API_BASE_URL}/api/v1/sites/water-concessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat, lng, radius_km: 25, nearest_fallback_radius_km: 75,
                               // Lets the backend pick the main-stem gauge on the site's own river.
                               river_name: s.river_name || s.lake_name || null })
      });
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}${body ? `: ${body.slice(0, 200)}` : ''}`);
      }
      const data = await response.json();
      setKonsesjonData(data);
    } catch (error) {
      console.error('Water concession fetch failed:', error);
      setKonsesjonError(`Could not fetch water concessions: ${error.message || 'unknown error'}`);
    } finally {
      setKonsesjonLoading(false);
    }
  };

  // DC cooling-water assessment fetcher. Posts site coords to
  // /sites/cooling-water; backend returns nearest river + cold
  // source + HydAPI flow + per-strategy viability assessment.
  const fetchCoolingWater = async () => {
    setCoolingWaterLoading(true);
    setCoolingWaterError(null);
    try {
      const s = analysis._site || {};
      const lat = analysis.location?.latitude ?? s.location?.latitude ?? s.lat ?? s.latitude ?? null;
      const lng = analysis.location?.longitude ?? s.location?.longitude ?? s.lon ?? s.longitude ?? null;
      if (lat == null || lng == null) throw new Error('No coordinates available for this site');
      const API_BASE_URL = import.meta.env.VITE_API_URL || '';
      // Pass the user's water-access threshold (from the search filter)
      // so the backend gates evaporative + cold-water viability against
      // it instead of the old hardcoded 1.4 km / 2.0 km caps. Omitted
      // when the user chose "water not required" — backend then reports
      // distance neutrally without a "Not viable" verdict.
      const userMaxWaterKm = analysis?._userMaxWaterKm;
      const body = (userMaxWaterKm != null)
        ? { lat, lng, max_water_distance_km: userMaxWaterKm }
        : { lat, lng };
      const response = await fetch(`${API_BASE_URL}/api/v1/sites/cooling-water`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}${body ? `: ${body.slice(0, 200)}` : ''}`);
      }
      const data = await response.json();
      setCoolingWaterData(data);
    } catch (error) {
      console.error('Cooling-water fetch failed:', error);
      setCoolingWaterError(`Could not fetch cooling-water assessment: ${error.message || 'unknown error'}`);
    } finally {
      setCoolingWaterLoading(false);
    }
  };

  // AI-based water concession assessment — POSTs the site to
  // /analyze-konsesjon which combines the technical site analysis
  // with the LLM regulatory agent. Returns approval probability,
  // showstoppers, recommendations. Rendered via <KonsesjonAssessment />.
  const runAiKonsesjon = async () => {
    setKonsesjonAiLoading(true);
    setKonsesjonAiError(null);
    try {
      const s = analysis._site || {};
      const lat = analysis.location?.latitude ?? s.location?.latitude ?? s.lat ?? s.latitude ?? null;
      const lng = analysis.location?.longitude ?? s.location?.longitude ?? s.lon ?? s.longitude ?? null;
      if (lat == null || lng == null) throw new Error('No coordinates available for this site');
      const API_BASE_URL = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/sites/analyze-konsesjon?version=v1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: { latitude: lat, longitude: lng },
          radius_km: 5.0,
          project_params: {
            power_mw: projectMwProp || 20,
            technology: useCase === 'data_center' ? 'colocation' : 'PEM',
          },
        }),
      });
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}${body ? `: ${body.slice(0, 200)}` : ''}`);
      }
      const data = await response.json();
      setKonsesjonAiData(data?.konsesjon_assessment || data);
    } catch (error) {
      console.error('AI konsesjon analysis failed:', error);
      setKonsesjonAiError(`AI analysis failed: ${error.message || 'unknown error'}`);
    } finally {
      setKonsesjonAiLoading(false);
    }
  };

  const _gridFeasParams = () => {
    const lat = analysis.location?.latitude
      ?? analysis._site?.location?.latitude
      ?? analysis._site?._zone_result?.nearest_grid?.lat
      ?? null;
    const lng = analysis.location?.longitude
      ?? analysis._site?.location?.longitude
      ?? analysis._site?._zone_result?.nearest_grid?.lon
      ?? null;
    if (!lat || !lng) throw new Error('No coordinates available for this site');
    // Coerce to clean numbers — upstream's /assess crashes with
    // "'<' not supported between instances of 'NoneType' and 'int'"
    // when its internal comparisons hit a NaN/None on the payload.
    // Hardening here so we never send anything but real integers
    // for the comparable fields, regardless of what state set the
    // analysis MW (defaults, user input, search-time inheritance).
    const mwRaw = projectMwProp || 20;
    const requestedMw = Math.max(1, Math.round(Number(mwRaw) || 20));
    const kvRaw = analysis._site?._zone_result?.nearest_grid?.voltage_level ?? analysis._site?.voltage_level_kv ?? 66;
    const connectionVoltageKv = Math.max(1, Math.round(Number(kvRaw) || 66));
    const voltageParam = connectionVoltageKv >= 420 ? '420kV'
                       : connectionVoltageKv >= 300 ? '300kV'
                       : connectionVoltageKv >= 132 ? '132kV'
                       :                              '66kV';
    // Upstream enum: 'hydrogen' | 'datacenter' | 'industrial' | 'battery' |
    // 'electrolysis' | 'ammonia' | 'steel' | 'aluminum' | 'other' | 'unknown'.
    // Our internal useCase value is 'data_center' (underscore) — upstream
    // rejects that with HTTP 422. Was also previously hardcoded to
    // 'hydrogen' which silently mis-typed every DC site (audit 2026-05-29).
    const projectType = useCase === 'data_center' ? 'datacenter'
                      : useCase === 'hydrogen'    ? 'hydrogen'
                      :                              'other';
    // Send the voltage + radius in EVERY form the upstream has used
    // historically. The /assess endpoint shares its substation-
    // proximity logic with /assess-station, which requires
    // `min_voltage_kv` + `radius_km` — when those are absent the
    // upstream defaults them to None and then crashes on
    // "'<' not supported between instances of 'NoneType' and 'int'"
    // (it compares the station's kV to min_voltage_kv to decide
    // whether the station is within voltage tier). We were sending
    // `connection_voltage` only, never `min_voltage_kv`, so every
    // call has been triggering that crash. Adding the right field
    // names below.
    return {
      lat,
      lng,
      requested_mw:          requestedMw,
      project_type:          projectType,
      // Voltage — send all known field names so the upstream picks
      // up whichever its current schema expects.
      connection_voltage:    voltageParam,           // legacy string
      connection_voltage_kv: connectionVoltageKv,    // numeric kV
      voltage_level_kv:      connectionVoltageKv,    // alt numeric kV
      min_voltage_kv:        connectionVoltageKv,    // what /assess-station uses
      // Search radius — required by the substation-proximity logic
      // shared with /assess-station. Matches our other call paths
      // (15 km is the default we pass to /assess-station from the
      // search-confirmed-capacity flow).
      radius_km:             15,
      project_window_years:  5,
    };
  };

  // All three grid-feasibility fetches now go through OUR backend at
  // /api/v1/grid-feasibility/{assess,queue,analyze} instead of directly
  // to seidr.makers.bot. The upstream returns "Disallowed CORS origin"
  // on browser preflight from seidr.site (and 404 on some redeploys),
  // both of which surface to the user as "Failed to fetch". The
  // proxy lives in backend/app/api/grid_feasibility_proxy.py and
  // surfaces upstream errors with a useful detail message instead of
  // the opaque fetch failure.
  const fetchGridFeasibility = async () => {
    setGridFeasLoading(true);
    setGridFeasError(null);
    try {
      const body = _gridFeasParams();
      const queueQs = new URLSearchParams({ lat: body.lat, lng: body.lng, radius_km: 15 }).toString();
      const API_BASE = import.meta.env.VITE_API_URL || '';
      // Independent settled — assess + queue are two different endpoints
      // with two different failure modes. Previously a single failure on
      // assess (e.g. our project_type bug, HTTP 422) threw before we ever
      // read the queue response — so the queue silently disappeared even
      // though it had returned 200 with a valid payload.
      const [assessResp, queueResp] = await Promise.allSettled([
        fetch(`${API_BASE}/api/v1/grid-feasibility/assess`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        }),
        fetch(`${API_BASE}/api/v1/grid-feasibility/queue?${queueQs}`),
      ]);
      // Queue first — independent of assess
      if (queueResp.status === 'fulfilled' && queueResp.value.ok) {
        try { setGridQueueData(await queueResp.value.json()); } catch {}
      }
      // Assess — capture error but don't unmount queue
      if (assessResp.status === 'fulfilled' && assessResp.value.ok) {
        try { setGridFeasData(await assessResp.value.json()); } catch {}
      } else {
        let detail = '';
        let status = null;
        if (assessResp.status === 'fulfilled') {
          status = assessResp.value.status;
          detail = await assessResp.value.json()
            .then(d => typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail || ''))
            .catch(() => `${status}`);
        } else {
          detail = assessResp.reason?.message || 'Network error';
        }
        // Upstream's own crash signature — surface a friendlier
        // message instead of raw Python traceback fragments.
        const isUpstreamBug = typeof detail === 'string' && (
          detail.includes("'<' not supported") ||
          detail.includes("NoneType") ||
          (status != null && status >= 500 && status < 600));
        if (isUpstreamBug) {
          setGridFeasError(
            'Grid feasibility service is temporarily unavailable ' +
            '(upstream returned an internal error). Try again in a few ' +
            'minutes — the queue data above is still valid.'
          );
        } else {
          setGridFeasError(`Grid feasibility (assess): ${detail.slice(0, 240)}`);
        }
      }
    } catch (e) {
      setGridFeasError(e.message || 'Request failed');
    } finally {
      setGridFeasLoading(false);
    }
  };

  // Auto-fetch grid feasibility + queue once per site selection (audit
  // 2026-05-29). User shouldn't have to click "Check grid feasibility &
  // queue" separately — by the time the panel is open we already know
  // they care about this site. The panel remounts on every coord change
  // (parent uses `key={lat-lon}`), so a single mount-time effect runs
  // once per site. Skips when coords / projectMw aren't ready yet,
  // when a fetch is already in flight, or when data was already
  // fetched (e.g. someone clicked the button before the effect ran).
  // Upstream is unreliable (seidr.makers.bot); failures still surface
  // through gridFeasError and don't block the rest of the panel.
  React.useEffect(() => {
    if (!analysis?._site) return;
    if (gridFeasData || gridFeasLoading || gridFeasError) return;
    try {
      const { lat, lng } = _gridFeasParams();
      if (lat == null || lng == null) return;
    } catch {
      return;   // _gridFeasParams throws when coords missing
    }
    fetchGridFeasibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis?._site]);

  const fetchGridAiAnalysis = async () => {
    setGridAiLoading(true);
    setGridAiError(null);
    try {
      const p = _gridFeasParams();
      // _gridFeasParams already returns the correct upstream project_type
      // (datacenter / hydrogen / other) — reuse it instead of re-mapping
      // with the wrong 'data_center' (underscore) value that hit HTTP 422.
      const reqBody = {
        lat: p.lat,
        lng: p.lng,
        requested_mw: p.requested_mw,
        project_type: p.project_type,
        project_window_years: p.project_window_years,
        connection_voltage: p.connection_voltage,
        include_advisors: ['queue_drop', 'timeline', 'reinforcement'],
      };
      const API_BASE = import.meta.env.VITE_API_URL || '';
      const resp = await fetch(`${API_BASE}/api/v1/grid-feasibility/analyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reqBody),
      });
      if (!resp.ok) {
        let detail = '';
        try {
          const errBody = await resp.json();
          if (Array.isArray(errBody?.detail)) {
            detail = errBody.detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join(' · ');
          } else if (typeof errBody?.detail === 'string') {
            detail = errBody.detail;
          } else {
            detail = JSON.stringify(errBody).slice(0, 300);
          }
        } catch { /* response not JSON */ }
        console.error('[grid-ai] request failed', { status: resp.status, detail });
        throw new Error(detail || `Grid AI error ${resp.status}`);
      }
      setGridAiData(await resp.json());
    } catch (e) {
      setGridAiError(e.message || 'Request failed');
    } finally {
      setGridAiLoading(false);
    }
  };

  // ─── Derive factual fields from analysis._site ────────────────────────────────
  const site = analysis._site || {};
  const z    = site._zone_result || {};
  const loc  = site._location_assessment || {};

  // Zone / municipality
  // For FI sites the "zone" is actually MML topographic land cover, not formal
  // asemakaava planning data — see backend mml_landcover_service. The Finnish
  // backend already returns a human-readable English name and keeps the
  // is_planning_zone=False flag, so we bypass the Norwegian PBL resolvers
  // (which would otherwise try to look up "taajaanrakennettualue" in
  // ZONE_NAMES_LOOKUP and return null).
  const _rawZoneCode     = z.zone_code ?? site.zoning_info?.zone_code ?? null;
  const _rawZoneName     = z.zone_name ?? site.zoning_info?.zone_name ?? null;
  const _isLandCover     = z.is_planning_zone === false || selectedCountry === 'FI';  // FI = land cover, NO/undefined = planning
  const zoneCode         = _isLandCover ? _rawZoneCode : resolveZoneCode(_rawZoneCode, _rawZoneName);
  const zoneName         = _isLandCover ? _rawZoneName : resolveZoneName(_rawZoneCode, _rawZoneName);
  const zoneConfidence   = z.zone_code_confidence ?? null;
  const zoneDataSource   = z.data_source ?? null;
  // NIBIO AR5 land cover (NO only) — always populated when the search
  // ran, complements DIBK plan data which often returns null in rural
  // areas. See backend ar5_landcover_service.py for the code → name
  // mapping. Renders as a separate "Land cover" row regardless of
  // whether DIBK returned anything.
  const ar5Code          = z.ar5_code ?? site.ar5_code ?? null;
  const ar5Name          = z.ar5_name ?? site.ar5_name ?? null;
  const ar5NameEn        = z.ar5_name_en ?? site.ar5_name_en ?? null;
  const totalAreaHa      = z.area_ha ?? site.total_parcel_area_ha ?? null;
  const municipalityNumber = z.municipality ?? site.municipality_number ?? null;
  const municipalityName = z.municipality_name ?? site.zoning_info?.municipality ?? null;
  const hasTvist         = site.har_tvist ?? site.tvist ?? false;

  // Grid connection — prefer the capacity-first context substation
  // when present (same source the result card displays and the user
  // saw tiered as sufficient/partial/inquiry). The local per-parcel
  // KNN against data_layers.grid_connections can resolve to a row
  // with the same name but a different voltage when there are two
  // physically separate substations sharing a name, which was
  // producing 132 kV on the card vs 420 kV on the panel for the
  // exact same site.
  const _cc = site._capacityContext;
  const _rawGridName     = _cc?.name ?? z.nearest_grid?.name ?? site.nearest_grid?.name ?? site.nearest_grid_name ?? '—';
  const gridVoltage      = _cc?.voltage_kv ?? z.nearest_grid?.voltage_level ?? site.voltage_level_kv ?? site.nearest_grid_voltage_kv ?? null;
  // Fallback display when the substation name is still useless after
  // the backend's local-name KNN — pulls operator + voltage +
  // station_id + coords so the analyst can identify WHICH substation
  // we anchored the distance criteria on. 2026-06-05.
  const gridName = (() => {
    if (_rawGridName && !/^(unknown|null|none|n\/a|—)$/i.test(String(_rawGridName).trim())) {
      return _rawGridName;
    }
    const op = _cc?.operator ?? site.nearest_grid_operator;
    const parts = [];
    if (op) parts.push(op);
    if (gridVoltage) parts.push(`${gridVoltage} kV`);
    if (_cc?.station_id) parts.push(`#${String(_cc.station_id).slice(-6)}`);
    const lat = _cc?.lat ?? site.nearest_grid?.lat ?? site.lat;
    const lng = _cc?.lng ?? _cc?.lon ?? site.nearest_grid?.lon ?? site.lon;
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      parts.push(`@ ${Number(lat).toFixed(3)}, ${Number(lng).toFixed(3)}`);
    }
    return parts.length > 0 ? `Unnamed — ${parts.join(' · ')}` : '—';
  })();
  // Operator (Nätägare). Backend now exposes nearest_grid_operator on the
  // top-level enrichment dict; fall back to nested grid object for older
  // saved sites.
  const gridOperator     = _cc?.operator
                          ?? site.nearest_grid_operator
                          ?? z.nearest_grid?.operator
                          ?? site.nearest_grid?.operator
                          ?? '—';
  const gridDistKm       = _cc?.distance_km ?? z.nearest_grid?.distance_km ?? site.grid_distance_km ?? site.nearest_grid_distance_km ?? null;
  const gridCapex        = gridCapexMnok(gridVoltage, gridDistKm);

  // Price zone — PPA for calculations, spot for reference display
  const { zone: priceZone, price_nok_mwh: electricityPrice, spot_nok_mwh: spotPrice } = getPriceZone(municipalityNumber);

  // Terrain & access
  const elevationM       = site.elevation_m ?? loc.elevation_m ?? null;
  const slopePct         = site.slope_pct   ?? loc.slope_pct   ?? null;
  const roadCategory     = site.road_category ?? null;
  const roadClassLabel   = site.road_class_label ?? null;
  const roadDistKm       = site.nearest_road_distance_km ?? site.road_distance_km ?? loc.road_distance_km ?? null;
  const bridgeLimit      = site.bridge_weight_limit_tonnes ?? null;
  // construction_season_months is the NO-pipeline field; the FI pipeline
  // populates frost_free_months from FMI climate normals (lat-band lookup).
  const constructionMonths = site.construction_season_months
    ?? site.frost_free_months
    ?? null;
  // FI climate normals (FMI 1991-2020 + Eurocode SFS-EN 1991-1-3 NA)
  const frostFreeDaysYr = site.frost_free_days_yr ?? null;
  const designTempMinC  = site.design_temp_min_c ?? null;
  const designTempMaxC  = site.design_temp_max_c ?? null;
  const snowLoadKnM2    = site.snow_load_kn_m2 ?? null;
  const freeCoolingDays = site.free_cooling_days_yr ?? null;

  // Geology (NGU, Norway only — bedrock, surface deposits, marine limit)
  const bedrockMainRock       = site.bedrock_main_rock ?? loc.bedrock_main_rock ?? null;
  const bedrockMainRockEn     = site.bedrock_main_rock_en ?? loc.bedrock_main_rock_en ?? null;
  const bedrockUnit           = site.bedrock_unit ?? loc.bedrock_unit ?? null;
  const bedrockFormationAge   = site.bedrock_formation_age ?? loc.bedrock_formation_age ?? null;
  const surfaceDepositName    = site.surface_deposit_name ?? loc.surface_deposit_name ?? null;
  const surfaceDepositRisk    = site.surface_deposit_risk ?? loc.surface_deposit_risk ?? null;
  const belowMarineLimit      = site.below_marine_limit ?? loc.below_marine_limit ?? null;
  const foundationRisk        = site.foundation_risk ?? loc.foundation_risk ?? null;
  const foundationRiskReasons = site.foundation_risk_reasons ?? loc.foundation_risk_reasons ?? [];

  // Port
  const portName              = site.port_name ?? loc.port_name ?? null;
  const portDistKm            = site.port_distance_km ?? loc.port_dist_km ?? site.port_dist_km ?? null;
  const portDepth             = site.port_depth_m ?? loc.port_depth_m ?? null;
  const portDepthEstimated    = site.port_depth_estimated ?? loc.port_depth_estimated ?? false;

  // Water
  const waterSourceName  = site.river_name ?? site.lake_name ?? loc.river_name ?? loc.lake_name ?? null;
  const waterDistKm      = site.water_distance_km ?? site.water_dist_km ?? loc.water_dist_km ?? null;
  const waterSourceType  = site.water_source_type ?? null;
  const waterCapacityClass = site.water_capacity_class ?? null;
  // FI-only flag: Overpass unreachable → show "Manual check" instead of
  // "Not identified" so the user can tell the difference between "no
  // water nearby" and "we couldn't ask".
  const fiWaterDataUnavailable = selectedCountry === 'FI'
    && site.fi_water_data_available === false;

  // SOP §17 — Data gap register. Build a list of services that returned
  // indeterminate / unavailable so the user knows what was NOT checked
  // vs what was checked-and-clear. Each entry is a short human string.
  //
  // The check pattern is `=== false || == null` because the backend
  // sometimes returns `data_available: false` explicitly and sometimes
  // just omits the key entirely on hard failures (e.g. service-level
  // exceptions). We treat both as "data missing" so the user always
  // sees what wasn't checked.
  const dataGaps = [];
  const fiMissing = (k) => site[k] === false || site[k] == null;
  if (selectedCountry === 'FI') {
    // Only flag a gap when the data truly wasn't fetched. If the actual
    // data field is populated (e.g. flood_zone is true/false), the
    // service succeeded even if the _data_available flag wasn't set.
    if (fiMissing('fi_hazard_data_available') && site.flood_zone == null) {
      dataGaps.push('Flood / landslide hazards: SYKE API unavailable or no result — verify manually');
    }
    if (site.fi_water_data_available === false && site.water_distance_km == null && !site.river_name && !site.lake_name) {
      dataGaps.push('Water source: Overpass unavailable — nearest lake/river not identified');
    }
    if (site.fi_protected_data_available === false && site.nearest_protected_distance_km == null) {
      dataGaps.push('Protected areas: Overpass unavailable — Natura 2000 / nature reserve check skipped');
    }
    if (site.fi_cultural_heritage_data_available === false && site.fi_cultural_heritage_count == null) {
      dataGaps.push('Cultural heritage: Museovirasto API unavailable — verify with kunta');
    }
    if (site.fi_noise_data_available === false && site.nearest_residential_distance_m == null) {
      dataGaps.push('Noise / residential receptor: Overpass unavailable — site noise study scope unknown');
    }
    if (site.elevation_m == null) {
      dataGaps.push('Elevation / slope: NLS terrain API gave no result for this parcel');
    }
    if (site.building_count == null) {
      dataGaps.push('Existing buildings: MML rakennus / Overpass count unavailable');
    }
    // SOP §10.4 — fiber proximity.
    if (site.nearest_ixp_distance_km == null && !site.nearest_ixp_name) {
      dataGaps.push('Fiber / IXP proximity: nearest IXP not computed for this site');
    }
    // SOP §8.5 — climate normals (lat-band lookup, should always work).
    // Don't flag if any climate field is present (they all come from the same lookup).
    if (site.design_temp_min_c == null && site.free_cooling_days_yr == null && site.frost_free_days_yr == null) {
      dataGaps.push('Climate design basis: FMI normals not attached to this site');
    }
  } else {
    if (site.hazard_data_available === false) {
      dataGaps.push('Flood / landslide / quick clay: NVE API unavailable — verify manually');
    }
  }
  const isSalmonRiver    = site.has_salmon ?? site.is_salmon_river ?? false;
  const hasExistingConcessions = site.has_water_concessions ?? false;
  const concessionCount  = site.water_concession_count ?? 0;

  // Environment
  // nin_type_a is boolean from /search; nin_type is a string from older pipeline
  const ninType          = site.nin_type ?? (site.nin_type_a ? (site.nin_type_a_name || 'A') : null);
  const culturalMonumentCount = site.cultural_monument_count
    ?? (site.has_automatically_protected_kulturminner ? 1 : (site.har_kulturminne ? 1 : 0));
  const protectedDistKm  = site.nearest_protected_distance_km ?? loc.protected_dist_km ?? null;
  const redListedCount   = site.red_listed_species_count ?? 0;
  const inReindeerArea   = site.in_reindeer_area ?? false;

  // Hazards — flood is now severity-tiered:
  //   "major"   = hard stop (detailed flood zone OR caution ≥30% overlap)
  //   "partial" = 5-30% caution overlap — flagged, not a hard stop
  //   "edge"    = <5% caution overlap — small stream clipping a corner
  //   "none"    = no flood polygons intersect the parcel
  const floodSeverity    = site.flood_severity ?? (
    site.flood_zone === true ? 'major' : site.flood_zone === false ? 'none' : null
  );
  const floodDetailedZone  = site.flood_detailed_zone ?? false;
  const floodCautionZone   = site.flood_caution_zone ?? (loc.flood_zone ? true : false);
  const floodDetailedPct   = site.flood_detailed_overlap_pct;  // number or null
  const floodCautionPct    = site.flood_caution_overlap_pct;
  const floodHardStop      = floodSeverity === 'major' || (site.flood_hard_stop ?? false);
  const landslidHardStop = site.landslide_hard_stop ?? false;
  const kvikkleireHardStop = site.kvikkleire_hard_stop ?? false;
  // Quick-clay severity (mirrors flood):
  //   major   = kildepunkt inside parcel → hard stop
  //   partial = kildepunkt within 50m of parcel edge → flag
  //   caution = kildepunkt 50-300m away → informational
  //   none    = nothing within 300m
  const kvikkleireSeverity    = site.kvikkleire_severity ?? null;
  const kvikkleirePointCount  = site.kvikkleire_point_count ?? 0;
  const kvikkleireNearestM    = site.kvikkleire_nearest_m;
  // Indeterminate state: when SYKE/NVE hazard checks failed (network/4xx)
  // we get back data_available=false. UI must show ⚠ "manual review needed"
  // instead of a green check, otherwise the user thinks the site has been
  // cleared when it actually hasn't been checked at all.
  const hazardDataAvailable = selectedCountry === 'FI'
    ? (site.fi_hazard_data_available !== false)
    : (site.hazard_data_available !== false);
  const hazardIndeterminate = !hazardDataAvailable;

  return (
    <div className="analysis-panel">
      <div className="panel-header">
        <h2>Site Analysis</h2>
        <div className="header-actions">
          {isPremium ? (
            <button onClick={exportToExcel} className="export-btn export-btn-excel" title="Export to Excel (.xlsx)">
              ⬇ Excel
            </button>
          ) : (
            <button
              onClick={() => openCalendly(auth0User)}
              className="export-btn export-btn-excel export-btn-premium-locked"
              title="Excel export is a Premium feature — click to book a call"
              aria-label="Excel export (Premium — opens Calendly)"
              style={{
                opacity: 0.45, cursor: 'pointer',
                position: 'relative',
              }}
            >
              ⬇ Excel
              <span style={{
                marginLeft: 6, fontSize: 9, fontWeight: 700,
                letterSpacing: '0.05em', textTransform: 'uppercase',
                color: '#fbbf24',
              }}>Premium</span>
            </button>
          )}
          <button onClick={onClose} className="close-btn">×</button>
        </div>
      </div>

      {/* Discovery context — only renders when the parcel was found via
          the capacity-first chain (SiteFinder attaches _discoverySubstation
          via ConfirmedCapacityResults). Tells the user which substation
          chain led here so the grid-side context isn't lost when they
          drill into the parcel detail. */}
      {(() => {
        const ds = analysis?._site?._discoverySubstation;
        if (!ds) return null;
        const capLabel = ds.available_cons_mw != null
          ? `capacity confirmed ${ds.available_cons_mw} MW`
          : 'capacity confirmed';
        return (
          <div style={{
            padding: '8px 16px', fontSize: 12,
            background: 'rgba(108,158,255,0.08)',
            borderBottom: '1px solid rgba(108,158,255,0.25)',
            color: '#cbd5e1',
          }}>
            <span style={{ color: '#94a3b8' }}>Connected via: </span>
            <span style={{ fontWeight: 600, color: '#f1f5f9' }}>{ds.name}</span>
            <span style={{ color: '#94a3b8' }}>
              {' '}· {ds.voltage_kv} kV · {ds.operator} · {capLabel}
            </span>
          </div>
        );
      })()}

      {/* Plant Capacity input removed from the panel header 2026-06-08
          per user request — MW is owned + edited from the SiteScreener
          sidebar (the same number that drove the search). Showing an
          editable copy in the panel created two sources of truth and
          made it ambiguous whether changing it here re-ran anything.
          onProjectMwChange callback is left intact so a future re-add
          (e.g. read-only chip) plugs in trivially. */}

      {/* Score breakdown block removed 2026-05-27 (restructure) —
          SiteVerdict shows the same headline score and SiteStory
          surfaces the per-theme detail, so this 100-line block
          was duplicate. Removing it also fixes the scroll bug:
          when it lived outside .panel-content it squeezed the
          scrollable area to ~0 px on smaller laptops. */}

      {enriching && (
        <div className="enrich-loading-banner">
          <span className="spinner enrich-spinner" />
          Loading data for this site…
        </div>
      )}
      {/* Only show "Kapasitet ikke satt" when MW was not set by the user AND this is not from a /search result */}
      {defaultMwNote && !enriching && !site._from_search && (
        <div className="enrich-default-mw-note">
          Capacity not set — showing estimate for 100 MW. Adjust above.
        </div>
      )}

      {/* View-mode toggle — analysis is the default landing tab (the
          deep verified-data + estimates view is the most valuable
          surface). Summary lives on its own tab so the 3-column
          strengths/concerns synthesis doesn't duplicate content. */}
      {/* Summary tab removed 2026-06-08 per user request — the
          SummaryView export-ready 1-pager remains available
          programmatically (e.g. for the Excel export pipeline) but
          isn't surfaced as a separate tab. Analysis + Data are the
          two views the analyst actually uses. */}
      <div className="view-toggle">
        {['analysis', 'data'].map(mode => (
          <button key={mode}
                  className={viewMode === mode ? 'active' : ''}
                  onClick={() => setViewMode(mode)}>
            {mode}
          </button>
        ))}
      </div>

      <div className="panel-content">
        <div className="overview-tab">

            {/* Verdict header — always visible across all three tabs
                (Summary / Analysis / Data). Audit 2026-05-27. */}
            {(site?._from_search || site?.readiness_score != null) && (
              <SiteVerdict site={site} useCase={useCase} />
            )}

            {/* DATA tab — moved here 2026-06-08 from the Analysis tab:
                VerifiedSiteData (search bar + collapsible groups +
                source citations), MissingData (action sections for
                Water catchment / Road / Grid feasibility / Species),
                and the Water Concessions / Cooling Water assessment
                blocks. This is the deep-data home. The flat DataView
                table was retired — VerifiedSiteData's grouped + search
                format covers the same information and is what users
                actually scan. */}
            {viewMode === 'data' && (
              <>
                <VerifiedSiteData
                  onLocate={onLocate}
                  site={site}
                  buildableDetail={buildableDetail}
                  buildableDetailLoading={buildableDetailLoading}
                  buildableDetailError={buildableDetailError}
                  onComputePrecise={fetchBuildableDetail}
                  jumpTargetField={jumpTargetField}
                  country={selectedCountry}
                />
                <MissingData site={site} jumpTargetField={jumpTargetField} />
              </>
            )}

            {viewMode === 'analysis' && (<>

            {/* Analysis tab — BottomLine + UseCase + verified data +
                estimates + missing data + grid feasibility. SiteStory
                (3-column synthesis) lives on the Summary tab to
                avoid duplicating content across tabs. */}
            {(site?._from_search || site?.readiness_score != null) && (
              <>
                <BottomLine  site={site} useCase={useCase} projectMw={projectMwProp || 100}
                             onJumpToField={handleJumpToField} />

                {/* Grid Feasibility & Queue — moved here 2026-06-05
                    so the connection-realism / queue-table / AI
                    analysis sits right under the BottomLine header
                    instead of buried at the bottom of the panel. */}
                <div className="analysis-section">
                  <div className="analysis-section-header">
                    Grid Feasibility &amp; Queue Analysis
                  </div>
                  {gridFeasLoading && (
                    <div style={{ fontSize: 11, color: '#94a3b8', padding: '4px 0' }}>
                      Fetching grid feasibility & queue…
                    </div>
                  )}
                  {gridFeasError && !(gridQueueData?.projects?.length > 0) && (
                    <div style={{
                      fontSize: 11, color: '#fca5a5',
                      background: 'rgba(220, 38, 38, 0.08)',
                      border: '1px solid rgba(220, 38, 38, 0.25)',
                      borderRadius: 4, padding: '8px 10px', marginTop: 8,
                      lineHeight: 1.5,
                    }}>
                      <strong>Grid feasibility service error:</strong> {gridFeasError}
                      <div style={{ marginTop: 4, opacity: 0.8 }}>
                        This service is provided by an external AI agent (seidr.makers.bot).
                        If the error persists, the upstream service may be offline.
                      </div>
                    </div>
                  )}
                  {!gridFeasData && gridQueueData?.projects?.length > 0 && (
                    <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '12px 14px', marginTop: 4 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
                        Queue projects within 15 km — {gridQueueData.total_queue_mw} MW total
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ color: '#64748b' }}>
                            <th style={{ textAlign: 'left', paddingBottom: 4, fontWeight: 500 }}>Project</th>
                            <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>MW</th>
                            <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>Drop prob.</th>
                            <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>Target</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gridQueueData.projects.map((p, i) => {
                            const dropColor = p.drop_probability_band === 'high' ? '#4ade80' : p.drop_probability_band === 'medium' ? '#fbbf24' : '#f87171';
                            return (
                              <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                <td style={{ padding: '4px 0', color: '#cbd5e1', lineHeight: 1.3 }}>
                                  {p.project_name}
                                  <span style={{ display: 'block', fontSize: 10, color: '#64748b' }}>{p.project_type}</span>
                                </td>
                                <td style={{ textAlign: 'right', color: '#e2e8f0', verticalAlign: 'top', paddingTop: 4 }}>{p.requested_mw}</td>
                                <td style={{ textAlign: 'right', color: dropColor, verticalAlign: 'top', paddingTop: 4 }}>{Math.round(p.drop_probability * 100)}%</td>
                                <td style={{ textAlign: 'right', color: '#64748b', verticalAlign: 'top', paddingTop: 4, fontSize: 10 }}>{p.target_connection_date?.slice(0, 7) || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ marginTop: 8, fontSize: 10, color: '#475569' }}>
                        {selectedCountry === 'FI' ? 'Fingrid' : 'Statnett'} queue data · live
                      </div>
                    </div>
                  )}
                  {gridFeasData && (() => {
                    const d = gridFeasData;
                    const ac = d.area_context || {};
                    const ttp = d.time_to_power || {};
                    const cr = d.connection_realism || {};
                    const qd = d.queue_drop || {};
                    const rd = d.reinforcement_dependency || {};
                    const bandColor = { very_low: '#f87171', low: '#fb923c', possible: '#fbbf24', likely: '#4ade80', high: '#4ade80' }[cr.band] || '#94a3b8';
                    return (
                      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '12px 14px', marginTop: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Connection realism</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: bandColor, background: `${bandColor}22`, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase' }}>
                            {cr.band?.replace('_', ' ') || '—'}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Connection within 5y</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{ttp.probability_5y != null ? `${Math.round(ttp.probability_5y * 100)}%` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Est. years to power</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{ttp.estimated_years != null ? `~${Number(ttp.estimated_years).toFixed(1)}y` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Congestion</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right', textTransform: 'capitalize' }}>{ac.congestion_level || '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Queue (MW)</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{ac.total_queue_mw != null ? `${ac.total_queue_mw} MW (${ac.queue_project_count} project${ac.queue_project_count !== 1 ? 's' : ''})` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Available capacity</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{ac.estimated_available_mw != null ? `${ac.estimated_available_mw} MW` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Queue drop probability</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{qd.probability != null ? `${Math.round(qd.probability * 100)}%` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Reinforcement needed</div>
                          <div style={{ fontSize: 11, color: rd.requires_reinforcement ? '#fb923c' : '#4ade80', textAlign: 'right' }}>{rd.requires_reinforcement == null ? '—' : rd.requires_reinforcement ? `Yes (${Math.round((rd.dependency || 0) * 100)}% dependency)` : 'No'}</div>
                        </div>
                        {gridQueueData?.projects?.length > 0 && (
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, marginTop: 4 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
                              Queue projects within 15 km — {gridQueueData.total_queue_mw} MW total
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                              <thead>
                                <tr style={{ color: '#64748b' }}>
                                  <th style={{ textAlign: 'left', paddingBottom: 4, fontWeight: 500 }}>Project</th>
                                  <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>MW</th>
                                  <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>Drop prob.</th>
                                  <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>Target</th>
                                </tr>
                              </thead>
                              <tbody>
                                {gridQueueData.projects.map((p, i) => {
                                  const dropColor = p.drop_probability_band === 'high' ? '#4ade80' : p.drop_probability_band === 'medium' ? '#fbbf24' : '#f87171';
                                  return (
                                    <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                      <td style={{ padding: '4px 0', color: '#cbd5e1', lineHeight: 1.3 }}>
                                        {p.project_name}
                                        <span style={{ display: 'block', fontSize: 10, color: '#64748b' }}>{p.project_type}</span>
                                      </td>
                                      <td style={{ textAlign: 'right', color: '#e2e8f0', verticalAlign: 'top', paddingTop: 4 }}>{p.requested_mw}</td>
                                      <td style={{ textAlign: 'right', color: dropColor, verticalAlign: 'top', paddingTop: 4 }}>{Math.round(p.drop_probability * 100)}%</td>
                                      <td style={{ textAlign: 'right', color: '#64748b', verticalAlign: 'top', paddingTop: 4, fontSize: 10 }}>{p.target_connection_date?.slice(0, 7) || '—'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {d.key_insights?.length > 0 && (
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, marginTop: 4 }}>
                            {d.key_insights.slice(0, 3).map((ins, i) => (
                              <div key={i} style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, lineHeight: 1.4 }}>· {ins}</div>
                            ))}
                          </div>
                        )}
                        {!gridAiData && (
                          <div style={{ marginTop: 10 }}>
                            <button className="cost-recalc-btn" onClick={fetchGridAiAnalysis} disabled={gridAiLoading} style={{ width: '100%' }}>
                              {gridAiLoading ? 'Running AI analysis… (may take 15 s)' : 'Get AI grid analysis'}
                            </button>
                            {gridAiError && <div style={{ fontSize: 11, color: '#f87171', marginTop: 4 }}>{gridAiError}</div>}
                          </div>
                        )}
                        {gridAiData?.analysis && (
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 10, paddingTop: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>AI Analysis</div>
                            <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.6 }}>
                              {gridAiData.analysis.split('\n').map((line, i) => {
                                if (line.startsWith('## ')) return <div key={i} style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 12, marginTop: 10, marginBottom: 4 }}>{line.replace('## ', '')}</div>;
                                if (line.startsWith('- **') || line.startsWith('- ')) return <div key={i} style={{ color: '#94a3b8', paddingLeft: 8, marginBottom: 2 }}>· {line.replace(/^- \*\*?/, '').replace(/\*\*?/, '')}</div>;
                                if (line.match(/^\d+\./)) return <div key={i} style={{ color: '#94a3b8', paddingLeft: 8, marginBottom: 2 }}>{line}</div>;
                                if (!line.trim() || line.startsWith('---') || line.startsWith('*Analysis')) return null;
                                return <div key={i} style={{ color: '#94a3b8', marginBottom: 2 }}>{line.replace(/\*\*/g, '')}</div>;
                              })}
                            </div>
                            <button onClick={() => setGridAiData(null)} style={{ fontSize: 10, color: '#475569', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0 0', display: 'block' }}>clear AI analysis</button>
                          </div>
                        )}
                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 10, color: '#475569' }}>{selectedCountry === 'FI' ? 'Fingrid' : 'Statnett'} queue data · {d.data_freshness?.queue_data || 'today'}</span>
                          <button onClick={() => { setGridFeasData(null); setGridQueueData(null); setGridAiData(null); }} style={{ fontSize: 10, color: '#475569', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>clear</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <UseCaseSuitability site={site} useCase={useCase} />

                {/* SiteStory — 3-column Strengths / Concerns / Data
                    Gaps synthesis. Moved here from the Summary tab
                    on 2026-06-08 per user request — analysts wanted
                    the at-a-glance synthesis in the same tab as the
                    deep analysis flow, not behind a separate tab. */}
                <SiteStory site={site} useCase={useCase} projectMw={projectMwProp || 100} onLocate={onLocate} />

                {/* Audit Phase 2: Section B — estimates, collapsed
                    by default, each row carries methodology. */}
                <EstimatesSection
                  site={site}
                  useCase={useCase}
                  projectMw={projectMwProp || 100}
                  buildableDetail={buildableDetail}
                />

                {/* VerifiedSiteData (search bar + grouped data rows)
                    and MissingData (Water Catchment / Road Access /
                    Grid Queue action sections) moved to the Data tab
                    on 2026-06-08 per user request. */}
              </>
            )}



                {/* Grid Feasibility & Queue section moved up to right
                    under the BottomLine header (2026-06-05). Old
                    bottom-of-panel render was deleted to avoid
                    rendering twice — see the block right after
                    BottomLine above. */}
                {false && (
                  <div hidden>
                  {gridFeasLoading && (
                    <div style={{ fontSize: 11, color: '#94a3b8', padding: '4px 0' }}>
                      Fetching grid feasibility & queue…
                    </div>
                  )}
                  {/* Show the assess error ONLY when the queue is
                      also missing — if queue data rendered above,
                      the analyst has what they need and the upstream
                      AI assessment's downtime is noise. */}
                  {gridFeasError && !(gridQueueData?.projects?.length > 0) && (
                    <div style={{
                      fontSize: 11, color: '#fca5a5',
                      background: 'rgba(220, 38, 38, 0.08)',
                      border: '1px solid rgba(220, 38, 38, 0.25)',
                      borderRadius: 4, padding: '8px 10px', marginTop: 8,
                      lineHeight: 1.5,
                    }}>
                      <strong>Grid feasibility service error:</strong> {gridFeasError}
                      <div style={{ marginTop: 4, opacity: 0.8 }}>
                        This service is provided by an external AI agent (seidr.makers.bot).
                        If the error persists, the upstream service may be offline.
                      </div>
                    </div>
                  )}
                  {/* Standalone queue panel — renders whenever /queue
                      succeeded, regardless of whether /assess crashed
                      on upstream's NoneType/int bug. The queue table
                      is independently useful (Statnett/Fingrid public
                      projects within 15 km) and should not disappear
                      just because the AI assessment service is down. */}
                  {!gridFeasData && gridQueueData?.projects?.length > 0 && (
                    <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '12px 14px', marginTop: 4 }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
                        Queue projects within 15 km — {gridQueueData.total_queue_mw} MW total
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ color: '#64748b' }}>
                            <th style={{ textAlign: 'left', paddingBottom: 4, fontWeight: 500 }}>Project</th>
                            <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>MW</th>
                            <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>Drop prob.</th>
                            <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>Target</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gridQueueData.projects.map((p, i) => {
                            const dropColor = p.drop_probability_band === 'high' ? '#4ade80' : p.drop_probability_band === 'medium' ? '#fbbf24' : '#f87171';
                            return (
                              <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                <td style={{ padding: '4px 0', color: '#cbd5e1', lineHeight: 1.3 }}>
                                  {p.project_name}
                                  <span style={{ display: 'block', fontSize: 10, color: '#64748b' }}>{p.project_type}</span>
                                </td>
                                <td style={{ textAlign: 'right', color: '#e2e8f0', verticalAlign: 'top', paddingTop: 4 }}>{p.requested_mw}</td>
                                <td style={{ textAlign: 'right', color: dropColor, verticalAlign: 'top', paddingTop: 4 }}>{Math.round(p.drop_probability * 100)}%</td>
                                <td style={{ textAlign: 'right', color: '#64748b', verticalAlign: 'top', paddingTop: 4, fontSize: 10 }}>{p.target_connection_date?.slice(0, 7) || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ marginTop: 8, fontSize: 10, color: '#475569' }}>
                        {selectedCountry === 'FI' ? 'Fingrid' : 'Statnett'} queue data · live
                      </div>
                    </div>
                  )}
                  {gridFeasData && (() => {
                    const d = gridFeasData;
                    const ac = d.area_context || {};
                    const ttp = d.time_to_power || {};
                    const cr = d.connection_realism || {};
                    const qd = d.queue_drop || {};
                    const rd = d.reinforcement_dependency || {};
                    const bandColor = { very_low: '#f87171', low: '#fb923c', possible: '#fbbf24', likely: '#4ade80', high: '#4ade80' }[cr.band] || '#94a3b8';
                    return (
                      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '12px 14px', marginTop: 4 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Connection realism</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: bandColor, background: `${bandColor}22`, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase' }}>
                            {cr.band?.replace('_', ' ') || '—'}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Connection within 5y</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{ttp.probability_5y != null ? `${Math.round(ttp.probability_5y * 100)}%` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Est. years to power</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{ttp.estimated_years != null ? `~${Number(ttp.estimated_years).toFixed(1)}y` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Congestion</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right', textTransform: 'capitalize' }}>{ac.congestion_level || '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Queue (MW)</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{ac.total_queue_mw != null ? `${ac.total_queue_mw} MW (${ac.queue_project_count} project${ac.queue_project_count !== 1 ? 's' : ''})` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Available capacity</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{ac.estimated_available_mw != null ? `${ac.estimated_available_mw} MW` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Queue drop probability</div>
                          <div style={{ fontSize: 11, color: '#e2e8f0', textAlign: 'right' }}>{qd.probability != null ? `${Math.round(qd.probability * 100)}%` : '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>Reinforcement needed</div>
                          <div style={{ fontSize: 11, color: rd.requires_reinforcement ? '#fb923c' : '#4ade80', textAlign: 'right' }}>{rd.requires_reinforcement == null ? '—' : rd.requires_reinforcement ? `Yes (${Math.round((rd.dependency || 0) * 100)}% dependency)` : 'No'}</div>
                        </div>
                        {gridQueueData?.projects?.length > 0 && (
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, marginTop: 4 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
                              Queue projects within 15 km — {gridQueueData.total_queue_mw} MW total
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                              <thead>
                                <tr style={{ color: '#64748b' }}>
                                  <th style={{ textAlign: 'left', paddingBottom: 4, fontWeight: 500 }}>Project</th>
                                  <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>MW</th>
                                  <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>Drop prob.</th>
                                  <th style={{ textAlign: 'right', paddingBottom: 4, fontWeight: 500 }}>Target</th>
                                </tr>
                              </thead>
                              <tbody>
                                {gridQueueData.projects.map((p, i) => {
                                  const dropColor = p.drop_probability_band === 'high' ? '#4ade80' : p.drop_probability_band === 'medium' ? '#fbbf24' : '#f87171';
                                  return (
                                    <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                      <td style={{ padding: '4px 0', color: '#cbd5e1', lineHeight: 1.3 }}>
                                        {p.project_name}
                                        <span style={{ display: 'block', fontSize: 10, color: '#64748b' }}>{p.project_type}</span>
                                      </td>
                                      <td style={{ textAlign: 'right', color: '#e2e8f0', verticalAlign: 'top', paddingTop: 4 }}>{p.requested_mw}</td>
                                      <td style={{ textAlign: 'right', color: dropColor, verticalAlign: 'top', paddingTop: 4 }}>{Math.round(p.drop_probability * 100)}%</td>
                                      <td style={{ textAlign: 'right', color: '#64748b', verticalAlign: 'top', paddingTop: 4, fontSize: 10 }}>{p.target_connection_date?.slice(0, 7) || '—'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {d.key_insights?.length > 0 && (
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, marginTop: 4 }}>
                            {d.key_insights.slice(0, 3).map((ins, i) => (
                              <div key={i} style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3, lineHeight: 1.4 }}>· {ins}</div>
                            ))}
                          </div>
                        )}
                        {!gridAiData && (
                          <div style={{ marginTop: 10 }}>
                            <button className="cost-recalc-btn" onClick={fetchGridAiAnalysis} disabled={gridAiLoading} style={{ width: '100%' }}>
                              {gridAiLoading ? 'Running AI analysis… (may take 15 s)' : 'Get AI grid analysis'}
                            </button>
                            {gridAiError && <div style={{ fontSize: 11, color: '#f87171', marginTop: 4 }}>{gridAiError}</div>}
                          </div>
                        )}
                        {gridAiData?.analysis && (
                          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 10, paddingTop: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>AI Analysis</div>
                            <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.6 }}>
                              {gridAiData.analysis.split('\n').map((line, i) => {
                                if (line.startsWith('## ')) return <div key={i} style={{ fontWeight: 700, color: '#e2e8f0', fontSize: 12, marginTop: 10, marginBottom: 4 }}>{line.replace('## ', '')}</div>;
                                if (line.startsWith('- **') || line.startsWith('- ')) return <div key={i} style={{ color: '#94a3b8', paddingLeft: 8, marginBottom: 2 }}>· {line.replace(/^- \*\*?/, '').replace(/\*\*?/, '')}</div>;
                                if (line.match(/^\d+\./)) return <div key={i} style={{ color: '#94a3b8', paddingLeft: 8, marginBottom: 2 }}>{line}</div>;
                                if (!line.trim() || line.startsWith('---') || line.startsWith('*Analysis')) return null;
                                return <div key={i} style={{ color: '#94a3b8', marginBottom: 2 }}>{line.replace(/\*\*/g, '')}</div>;
                              })}
                            </div>
                            <button onClick={() => setGridAiData(null)} style={{ fontSize: 10, color: '#475569', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0 0', display: 'block' }}>clear AI analysis</button>
                          </div>
                        )}
                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 10, color: '#475569' }}>{selectedCountry === 'FI' ? 'Fingrid' : 'Statnett'} queue data · {d.data_freshness?.queue_data || 'today'}</span>
                          <button onClick={() => { setGridFeasData(null); setGridQueueData(null); setGridAiData(null); }} style={{ fontSize: 10, color: '#475569', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>clear</button>
                        </div>
                      </div>
                    );
                  })()}
                  </div>
                )}

            </>)}{/* end viewMode === 'analysis' for the upper blocks */}

            {/* Water Concessions + DC Cooling Water — back on the
                Analysis tab (2026-10-05, product call): for an ammonia /
                hydrogen plant the water-rights picture is part of the
                verdict, not a drill-down. */}
            {viewMode === 'analysis' && (<>

                {/* ── Water Concessions (raw NVE data) ──────────────────────
                    NO-only. Calls /api/v1/sites/water-concessions which
                    fetches the actual NVE Vannkraft hydropower plants +
                    Konsesjonsvilkår concession entries + Verneplan
                    vassdrag protection status. Replaced the LLM
                    /analyze-konsesjon panel 2026-06-04 — users wanted
                    the actual concessions (plant name, MW, owner, river,
                    concession dates) rather than the AI summary.
                    On-click; fast (~3-8 s for 3 parallel NVE calls). */}
                {/* Hydropower + Konsesjonsvilkår + Verneplan answer
                    "what hydroelectric infrastructure and water-rights
                    constraints surround this site" — relevant for
                    hydrogen (water rights, PPA economics, salmon-river
                    exclusion) but irrelevant for DC cooling decisions.
                    Hide for DC and surface a separate cooling-water
                    section below instead. */}
                {selectedCountry === 'NO' && useCase !== 'data_center' && (
                  <div className="analysis-section">
                    <div className="analysis-section-header">
                      Water Concessions (NVE)
                    </div>
                    {!konsesjonData && (
                      <button
                        className="cost-recalc-btn"
                        onClick={analyzeKonsesjon}
                        disabled={konsesjonLoading}
                        style={{
                          width: '100%', padding: '10px 14px', fontSize: 12, fontWeight: 600,
                          borderRadius: 6, color: '#fff',
                          background: konsesjonLoading ? '#1f2937' : 'linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)',
                          border: '1px solid #2563eb',
                          cursor: konsesjonLoading ? 'wait' : 'pointer',
                        }}
                      >
                        {konsesjonLoading ? 'Fetching concessions…' : 'Fetch water concessions'}
                      </button>
                    )}
                    {konsesjonError && (
                      <div style={{ marginTop: 8, padding: 10, borderRadius: 6, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.4)', color: '#fca5a5', fontSize: 11 }}>
                        {konsesjonError}
                        <button onClick={() => setKonsesjonError(null)} style={{ marginLeft: 8, fontSize: 10, background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', textDecoration: 'underline' }}>dismiss</button>
                      </div>
                    )}
                    {konsesjonData && (() => {
                      const vp     = konsesjonData.verneplan || {};
                      const hp     = konsesjonData.hydropower || { plants: [] };
                      const cs     = konsesjonData.concessions || { concessions: [] };
                      const radius = konsesjonData.radius_km || 15;
                      // Pull likely date fields out of each Konsesjonsvilkår
                      // attribute dict — the NVE service returns the full
                      // attribute set so field names vary. Try the common
                      // SOSI-style names and fall back to the raw value
                      // when none match.
                      const dateField = (rec) =>
                        rec?.gyldigTilDato || rec?.tildeltDato || rec?.konsesjonDato ||
                        rec?.fraDato || rec?.tilDato || rec?.dato || null;
                      const ownerField = (rec) =>
                        rec?.konsesjonInnehaver || rec?.eier || rec?.kraftverkEier ||
                        rec?.virksomhetNavn || rec?.firma || null;
                      const purposeField = (rec) =>
                        rec?.formal || rec?.tema || rec?.type || rec?.beskrivelse || null;
                      return (
                        <div style={{ marginTop: 4, fontSize: 12 }}>
                              {/* Regulated reservoirs (NVE Magasin) near the site — if the nearest
                                  lake is one of these it already carries hydropower concession
                                  terms (HRV/LRV); abstraction competes with that operator. */}
                              {(() => {
                                const rv = konsesjonData.reservoirs || { reservoirs: [] };
                                if (!rv.reservoirs || rv.reservoirs.length === 0) {
                                  return (
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                                      No regulated hydropower reservoir within {rv.radius_km ?? 2} km (NVE Magasin).
                                    </div>
                                  );
                                }
                                return (
                                  <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#fbbf24', marginBottom: 4 }}>
                                      Regulated reservoir within {rv.radius_km ?? 2} km ({rv.count})
                                    </div>
                                    {rv.reservoirs.map((r, i) => (
                                      <div key={i} style={{ fontSize: 11, color: '#fde68a', padding: '4px 10px', borderLeft: '3px solid #fbbf24', background: 'rgba(251,191,36,0.06)', marginBottom: 4, borderRadius: 4, cursor: (onLocate && r.lat != null) ? 'pointer' : undefined }}
                                           title={(onLocate && r.lat != null) ? 'Show on map' : undefined}
                                           onClick={(onLocate && r.lat != null) ? () => onLocate('reservoir', r.name, `${r.name || 'Reservoir'} (regulated reservoir)`, { lat: r.lat, lon: r.lon }) : undefined}>
                                        {(onLocate && r.lat != null) && <span style={{ color: '#60a5fa', marginRight: 5 }}>⌖</span>}<span style={{ fontWeight: 600 }}>{r.name || 'Unnamed reservoir'}</span>
                                        {r.hrv_m != null && <span> · HRV {r.hrv_m} m</span>}
                                        {r.lrv_m != null && <span> · LRV {r.lrv_m} m</span>}
                                        {r.status && <span> · {r.status}</span>}
                                        {r.commissioned && <span> · since {r.commissioned}</span>}
                                      </div>
                                    ))}
                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>Water drawn from a regulated reservoir needs the operator's and NVE's consent.</div>
                                  </div>
                                );
                              })()}
                          {/* What water can THIS plant use? One synthesis line per option,
                              built from the measured fields + the NVE lookups below. Legal
                              framing is deliberately cautious — every option says what to
                              verify. */}
                          {(() => {
                            const mw = Number(projectMwProp || 100);
                            const demandM3h = mw * 0.8;
                            const coastKm = site.sea_name ? (site.water_distance_km ?? site.nearest_water_distance_km) : null;
                            const portKm = site.nearest_port_distance_km ?? site.port_dist_km;
                            const seaKm = coastKm != null ? coastKm : (portKm != null ? portKm : null);
                            const fresh = site.river_name || site.lake_name;
                            const freshKm = (!site.sea_name) ? (site.water_distance_km ?? site.nearest_water_distance_km) : null;
                            const rv = konsesjonData.reservoirs || {};
                            const fl = konsesjonData.flow;
                            // Gauge flow only applies when the gauge is on the SAME river as
                            // the source. A creek 12 km away says nothing about a lake.
                            const gaugeApplies = !!(fl && fl.data_quality === 'measured' && fl.match === 'same_river' && site.river_name);
                            const low = gaugeApplies && fl.p10_m3s != null ? Number(fl.p10_m3s) : null;
                            const share = low ? ((demandM3h / 3600) / low) * 100 : null;
                            const shareWord = share == null ? null : share < 1 ? 'minor' : share < 10 ? 'material' : 'substantial';
                            const nUsers = (konsesjonData.concessions?.count || 0) + (konsesjonData.concessions?.nearest_outside_primary_radius?.count || 0);
                            const Row = ({ ok, title, body, go }) => (
                              <div style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 12, cursor: go ? 'pointer' : undefined }}
                                   onClick={go || undefined} title={go ? 'Show on map' : undefined}>
                                <span style={{ color: ok === true ? '#4ade80' : ok === false ? '#f87171' : '#fbbf24', fontWeight: 700, minWidth: 14 }}>{ok === true ? '✓' : ok === false ? '✕' : '?'}</span>
                                <div><div style={{ fontWeight: 600, color: '#e2e8f0' }}>{go && <span style={{ color: '#60a5fa', marginRight: 5 }}>⌖</span>}{title}</div><div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{body}</div></div>
                              </div>
                            );
                            const goCoast = onLocate ? () => onLocate('coast', '*', 'Nearest shoreline') : null;
                            const goFresh = onLocate && fresh
                              ? (site.river_name ? () => onLocate('river', site.river_name, `${site.river_name} (river)`)
                                                 : () => onLocate('lake', site.lake_name, `${site.lake_name} (lake)`))
                              : null;
                            return (
                              <div style={{ marginBottom: 12 }}>
                                <div style={{ fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>
                                  Water sourcing options at {mw} MW
                                  <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>process demand ≈ {demandM3h.toFixed(0)} m³/h (0.8 m³/h per MW; cooling extra)</span>
                                </div>
                                <Row ok={seaKm != null && seaKm <= 5 ? true : seaKm != null ? null : null} go={goCoast}
                                     title={seaKm != null ? `Seawater via desalination — coast ≈ ${Number(seaKm).toFixed(1)} km` : 'Seawater — distance to coast not measured'}
                                     body={seaKm != null && seaKm <= 5
                                       ? 'No freshwater abstraction concession needed. Requires a discharge permit for brine/cooling water (Statsforvalteren, forurensningsloven) and intake/outfall consent (Kystverket, havne- og farvannsloven). Standard choice for coastal ammonia plants. Verify.'
                                       : 'Beyond ~5 km a seawater pipeline is usually uneconomic for process water alone.'} />
                                <Row ok={fresh ? (konsesjonData.verneplan?.in_verneplan ? false : (rv.count > 0 ? null : (share != null ? (share < 1 ? true : share < 10 ? null : false) : null))) : null} go={goFresh}
                                     title={fresh ? `Freshwater from ${fresh}${freshKm != null ? ` — ${Number(freshKm).toFixed(1)} km` : ''}` : 'Freshwater — no lake or river measured'}
                                     body={[
                                       konsesjonData.verneplan?.in_verneplan ? 'Protected watercourse (Verneplan): new abstraction concessions are not normally granted.' : null,
                                       rv.count > 0 ? `Regulated hydropower reservoir within ${rv.radius_km ?? 2} km (${(rv.reservoirs || []).map(r => r.name).filter(Boolean).join(', ') || 'unnamed'}): abstraction competes with the operator's concession terms.` : null,
                                       share != null
                                         ? `Demand is ${share < 0.01 ? '<0.01' : share.toFixed(2)}% of low flow at the ${fl.station_name} gauge on this river — ${shareWord}. ${share < 1 ? 'NVE assesses abstractions under vannressursloven §8; minor uses may only need notification.' : share < 10 ? 'A concession under vannressursloven §8 will be needed and ecological minimum flow will be a condition.' : 'Unlikely to be granted without storage or a regulated source.'} Verify with NVE.`
                                         : (site.lake_name && !site.river_name
                                             ? 'No gauge on this lake — an abstraction is assessed on lake inflow and volume, which are not measured here.'
                                             : (fl && fl.match === 'nearest' ? 'Nearest gauge is on a different catchment, so no flow share can be given.' : (fl ? 'No gauge flow data for this source.' : 'Gauge flow unavailable.'))),
                                       nUsers > 0 ? `${nUsers} registered abstraction${nUsers === 1 ? '' : 's'} nearby (see list below) — existing users are the comparables for a permit.` : 'No registered abstractions nearby in NVE Vannuttak.',
                                     ].filter(Boolean).join(' ')} />
                                <Row ok={null} title="Municipal water network" body="Capacity and tariff not assessed — ask the kommune's VA-etat; typical for demin make-up at this scale." />
                              </div>
                            );
                          })()}

                          {/* Gauge flow (NVE HydAPI) vs this plant's water demand — the
                              number an abstraction permit is judged on. */}
                          {(() => {
                            const fl = konsesjonData.flow;
                            if (!fl) {
                              return (
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
                                  River flow unavailable: {konsesjonData.flow_error || 'no gauge data'}.
                                </div>
                              );
                            }
                            if (fl.data_quality !== 'measured' || fl.p50_m3s == null) {
                              return (
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
                                  River flow: no active NVE gauge with data near this site ({fl.data_quality}).
                                </div>
                              );
                            }
                            // Demand: 0.8 m³/h per MW (the search's seawater/freshwater
                            // assumption) → m³/s. Compared with the LOW flow (P10),
                            // which is what a permit is assessed against.
                            const mw = Number(projectMwProp || 100);
                            const demandM3s = (mw * 0.8) / 3600;
                            const low = fl.p10_m3s != null ? Number(fl.p10_m3s) : null;
                            const share = low ? (demandM3s / low) * 100 : null;
                            const applies = fl.match === 'same_river';
                            const goGauge = (onLocate && fl.lat != null) ? () => onLocate('gauge', fl.station_name, `${fl.station_name} (NVE gauge)`, { lat: fl.lat, lon: fl.lon }) : null;
                            return (
                              <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 6, background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.3)', cursor: goGauge ? 'pointer' : undefined }}
                                   onClick={goGauge || undefined} title={goGauge ? 'Show gauge on map' : undefined}>
                                <div style={{ fontWeight: 600, color: '#bfdbfe', fontSize: 12 }}>
                                  {goGauge && <span style={{ color: '#60a5fa', marginRight: 5 }}>⌖</span>}Gauge {fl.station_name}{fl.river_name && fl.river_name !== fl.station_name ? ` (${fl.river_name})` : ''} · {Number(fl.distance_km).toFixed(1)} km{fl.match === 'same_river' ? ' · same river, upstream' : fl.match === 'nearest' ? ' · nearest gauge, different catchment' : ''}
                                </div>
                                <div style={{ fontSize: 11, color: '#dbeafe', marginTop: 3 }}>
                                  Median flow {Number(fl.p50_m3s).toFixed(1)} m³/s · low flow (P10) {low != null ? low.toFixed(1) : '—'} m³/s · high (P90) {fl.p90_m3s != null ? Number(fl.p90_m3s).toFixed(1) : '—'} m³/s
                                  {fl.catchment_km2 != null && <span> · catchment {Math.round(fl.catchment_km2)} km²</span>}
                                </div>
                                <div style={{ fontSize: 11, color: '#e0f2fe', marginTop: 3 }}>
                                  Plant demand at {mw} MW ≈ {(demandM3s * 3600).toFixed(0)} m³/h = {demandM3s.toFixed(4)} m³/s
                                  {applies && share != null && <span> → <strong>{share < 0.01 ? '<0.01' : share.toFixed(2)}% of low flow</strong></span>}
                                  {!applies && <span className="gfb-muted"> — share not shown: gauge is not on this site's source</span>}
                                </div>
                                {fl.match === 'nearest' && fl.catchment_km2 != null && fl.catchment_km2 < 10 && (
                                  <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4 }}>
                                    This gauge sits on a creek with a {Number(fl.catchment_km2).toFixed(1)} km² catchment — not a usable source at this scale. No gauged river near the site; for a coastal site, seawater is the realistic option.
                                  </div>
                                )}
                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>NVE HydAPI percentiles (median across the year of each daily percentile). Demand uses 0.8 m³/h per MW; a cooling-water design will differ.</div>
                              </div>
                            );
                          })()}
                          {/* Verneplan status */}
                          <div style={{
                            marginBottom: 12, padding: '8px 10px', borderRadius: 6,
                            background: vp.in_verneplan ? 'rgba(248,113,113,0.10)' : 'rgba(34,197,94,0.08)',
                            border: `1px solid ${vp.in_verneplan ? 'rgba(248,113,113,0.4)' : 'rgba(34,197,94,0.4)'}`,
                          }}>
                            <div style={{ fontWeight: 600, color: vp.in_verneplan ? '#fca5a5' : '#86efac' }}>
                              {vp.in_verneplan

                                ? `⚠ Inside protected watershed (Verneplan ${vp.tier ? 'tier ' + vp.tier : ''})`
                                : '✓ Not in a protected watershed'}
                            </div>
                            {vp.in_verneplan && vp.name && (
                              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
                                {vp.name}{vp.area_km2 ? ` · ${vp.area_km2.toFixed?.(1) ?? vp.area_km2} km²` : ''}{vp.vassdrag_nr ? ` · vassdrag ${vp.vassdrag_nr}` : ''}
                              </div>
                            )}
                          </div>

                          {/* Nearby hydropower plants */}
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 600, color: '#e2e8f0', marginBottom: 6 }}>
                              Hydropower plants within {radius} km
                              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>
                                ({hp.count || 0} found{hp.total_mw ? `, total ${hp.total_mw} MW` : ''}{(hp.count || 0) > (hp.plants || []).length ? ` · showing top ${(hp.plants || []).length}` : ''})
                              </span>
                            </div>
                            {(hp.plants || []).length === 0 ? (
                              <div>
                                <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
                                  No active hydropower plants in range.
                                </div>
                                {hp.nearest_outside_primary_radius?.plants?.length > 0 && (
                                  <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                                      Nearest plants within {hp.nearest_outside_primary_radius.radius_km} km
                                      <span style={{ color: '#64748b', marginLeft: 6 }}>
                                        ({hp.nearest_outside_primary_radius.count} total,
                                        {hp.nearest_outside_primary_radius.total_mw} MW)
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      {hp.nearest_outside_primary_radius.plants.map((p, i) => (
                                        <div key={i} style={{
                                          padding: '6px 10px', borderRadius: 4,
                                          background: 'rgba(255,255,255,0.025)',
                                          borderLeft: '2px dashed #475569',
                                          cursor: (onLocate && p.lat != null) ? 'pointer' : undefined,
                                        }}
                                        title={(onLocate && p.lat != null) ? 'Show on map' : undefined}
                                        onClick={(onLocate && p.lat != null) ? () => onLocate('hydropower', p.name, `${p.name} · ${p.mw ?? '?'} MW`, { lat: p.lat, lon: p.lon }) : undefined}>
                                          <div style={{ fontWeight: 600, color: '#cbd5e1' }}>
                                            {p.name || '—'}
                                            {p.mw != null && <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 8 }}>{p.mw} MW</span>}
                                          </div>
                                          <div style={{ fontSize: 11, color: '#64748b' }}>
                                            {p.owner && <span>Owner: {p.owner}</span>}
                                            {p.owner && p.river && <span> · </span>}
                                            {p.river && <span>{p.river}</span>}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {hp.plants.map((p, i) => (
                                  <div key={i} style={{
                                    padding: '6px 10px', borderRadius: 4,
                                    background: 'rgba(255,255,255,0.04)',
                                    borderLeft: '3px solid #60a5fa',
                                    cursor: (onLocate && p.lat != null) ? 'pointer' : undefined,
                                  }}
                                  title={(onLocate && p.lat != null) ? 'Show on map' : undefined}
                                  onClick={(onLocate && p.lat != null) ? () => onLocate('hydropower', p.name, `${p.name} · ${p.mw ?? '?'} MW`, { lat: p.lat, lon: p.lon }) : undefined}>
                                    <div style={{ fontWeight: 600, color: '#dbeafe' }}>
                                      {p.name || '—'}
                                      {p.mw != null && <span style={{ fontWeight: 400, color: '#93c5fd', marginLeft: 8 }}>{p.mw} MW</span>}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
                                      {p.owner && <span>Owner: {p.owner}</span>}
                                      {p.owner && p.river && <span> · </span>}
                                      {p.river && <span>{p.river}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Konsesjonsvilkår concession entries */}
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontWeight: 600, color: '#e2e8f0', marginBottom: 6 }}>
                              Registered water abstractions (NVE Vannuttak) within {radius} km
                              <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>
                                ({cs.count || 0} found)
                              </span>
                            </div>
                            {(cs.concessions || []).length === 0 ? (
                              <div>
                                <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
                                  No registered abstraction points in range. NVE's concession-terms layer is no longer publicly queryable, so this is not proof that no water concession exists — check NVE konsesjonssøk for the source itself.
                                </div>
                                {cs.nearest_outside_primary_radius?.concessions?.length > 0 && (
                                  <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                                      Existing water users near source (within {cs.nearest_outside_primary_radius.radius_km} km)
                                      <span style={{ color: '#64748b', marginLeft: 6 }}>
                                        ({cs.nearest_outside_primary_radius.count} total)
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      {cs.nearest_outside_primary_radius.concessions.map((c, i) => {
                                        const owner = ownerField(c);
                                        const dt    = dateField(c);
                                        const purp  = purposeField(c);
                                        const id    = c?.navn || c?.objektnavn || c?.tittel || c?.konsesjonsNr || c?.VV_vilkarStedbeskrivelse || c?.vannAnleggNavn || c?.beskrivelse || `Water abstraction ${i + 1}`;
                                        return (
                                          <div key={i} style={{
                                            padding: '6px 10px', borderRadius: 4,
                                            background: 'rgba(255,255,255,0.025)',
                                            borderLeft: '2px dashed #475569',
                                            cursor: (onLocate && c?.lat != null) ? 'pointer' : undefined,
                                          }}
                                          title={(onLocate && c?.lat != null) ? 'Show on map' : undefined}
                                          onClick={(onLocate && c?.lat != null) ? () => onLocate('abstraction', id, `${id} (${purp || 'water user'})`, { lat: c.lat, lon: c.lon }) : undefined}>
                                            <div style={{ fontWeight: 600, color: '#cbd5e1' }}>{(onLocate && c?.lat != null) && <span style={{ color: '#60a5fa', marginRight: 5 }}>⌖</span>}{id}</div>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>
                                              {owner && <span>{owner}</span>}
                                              {owner && (dt || purp) && <span> · </span>}
                                              {dt && <span>{dt}</span>}
                                              {dt && purp && <span> · </span>}
                                              {purp && <span>{purp}</span>}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {cs.concessions.map((c, i) => {
                                  const owner = ownerField(c);
                                  const dt    = dateField(c);
                                  const purp  = purposeField(c);
                                  // Identifier — try the obvious names; fall back to the
                                  // first short string-typed field in the attribute dict.
                                  const id    = c?.navn || c?.objektnavn || c?.tittel || c?.konsesjonsNr || c?.VV_vilkarStedbeskrivelse || c?.vannAnleggNavn || c?.beskrivelse || `Water abstraction ${i + 1}`;
                                  return (
                                    <div key={i} style={{
                                      padding: '6px 10px', borderRadius: 4,
                                      background: 'rgba(255,255,255,0.04)',
                                      borderLeft: '3px solid #a78bfa',
                                      cursor: (onLocate && c?.lat != null) ? 'pointer' : undefined,
                                    }}
                                    title={(onLocate && c?.lat != null) ? 'Show on map' : undefined}
                                    onClick={(onLocate && c?.lat != null) ? () => onLocate('abstraction', id, `${id} (water abstraction)`, { lat: c.lat, lon: c.lon }) : undefined}>
                                      <div style={{ fontWeight: 600, color: '#e9d5ff' }}>{id}</div>
                                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>
                                        {owner && <span>{owner}</span>}
                                        {owner && (dt || purp) && <span> · </span>}
                                        {dt && <span>{dt}</span>}
                                        {dt && purp && <span> · </span>}
                                        {purp && <span>{purp}</span>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 10, color: '#475569' }}>NVE Vannkraft · Konsesjonsvilkår · Verneplan vassdrag</span>
                            <button onClick={() => setKonsesjonData(null)} style={{ fontSize: 10, color: '#475569', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>clear</button>
                          </div>
                        </div>
                      );
                    })()}

                  </div>
                )}

            </>)}{/* end viewMode === 'data' for Water Concessions */}

            {/* ── DC Cooling Water Assessment ──────────────────────
                DC-only. Moved back to the Analysis tab 2026-06-08
                per user request — cooling viability is an analysis-
                stage question, not a data drill-down. Water
                Concessions (hydrogen path) stays on Data above. */}
            {viewMode === 'analysis' && selectedCountry === 'NO' && useCase === 'data_center' && (
                  <div className="analysis-section">
                    <div className="analysis-section-header">
                      Cooling Water Assessment
                    </div>
                    {!coolingWaterData && (
                      <button
                        className="cost-recalc-btn"
                        onClick={fetchCoolingWater}
                        disabled={coolingWaterLoading}
                        style={{
                          width: '100%', padding: '10px 14px', fontSize: 12, fontWeight: 600,
                          borderRadius: 6, color: '#fff',
                          background: coolingWaterLoading ? '#1f2937' : 'linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)',
                          border: '1px solid #2563eb',
                          cursor: coolingWaterLoading ? 'wait' : 'pointer',
                        }}
                      >
                        {coolingWaterLoading ? 'Assessing cooling water…' : 'Assess cooling-water viability'}
                      </button>
                    )}
                    {coolingWaterError && (
                      <div style={{ marginTop: 8, padding: 10, borderRadius: 6, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.4)', color: '#fca5a5', fontSize: 11 }}>
                        {coolingWaterError}
                        <button onClick={() => setCoolingWaterError(null)} style={{ marginLeft: 8, fontSize: 10, background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', textDecoration: 'underline' }}>dismiss</button>
                      </div>
                    )}
                    {coolingWaterData && (() => {
                      const cw = coolingWaterData;
                      const fresh = cw.nearest_freshwater;
                      const cold = cw.nearest_cold_source;
                      const flow = cw.flow;
                      const a = cw.cooling_assessment || {};
                      const StrategyRow = ({ name, label, data }) => {
                        if (!data) return null;
                        const color = data.viable ? '#22c55e' : '#94a3b8';
                        return (
                          <div style={{
                            padding: '8px 10px', borderRadius: 4, marginBottom: 6,
                            background: 'rgba(255,255,255,0.04)',
                            borderLeft: `3px solid ${color}`,
                          }}>
                            <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
                              <span>{label}</span>
                              <span style={{ color, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {data.viable ? 'Viable' : 'Not viable'}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, lineHeight: 1.5 }}>
                              {data.note}
                            </div>
                          </div>
                        );
                      };
                      return (
                        <div>
                          {/* Underlying signals */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: 10, fontSize: 11 }}>
                            <div style={{ color: '#94a3b8' }}>Nearest river/lake</div>
                            <div style={{ color: '#e2e8f0', textAlign: 'right' }}>
                              {fresh ? `${fresh.name || fresh.type} · ${fresh.distance_km} km` : '—'}
                            </div>
                            <div style={{ color: '#94a3b8' }}>Nearest cold source (sea)</div>
                            <div style={{ color: '#e2e8f0', textAlign: 'right' }}>
                              {cold ? `${cold.distance_km} km` : '—'}
                            </div>
                            <div style={{ color: '#94a3b8' }}>P50 flow (HydAPI)</div>
                            <div style={{ color: '#e2e8f0', textAlign: 'right' }}>
                              {flow?.avg_flow_m3s != null
                                ? `${flow.avg_flow_m3s.toFixed(2)} m³/s @ ${flow.station_name} (${flow.distance_km} km)`
                                : (flow?.data_quality === 'no_station' ? 'No station within 25 km' : '—')}
                            </div>
                          </div>
                          <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                            Strategy viability
                          </div>
                          <StrategyRow name="dx" label="Free-cooling DX" data={a.free_cooling_dx} />
                          <StrategyRow name="evap" label="Evaporative cooling" data={a.evaporative} />
                          <StrategyRow name="cold" label="Direct cold-water cooling" data={a.direct_cold_water} />
                          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 10, color: '#475569' }}>NVE Elvenett / Innsjø · Kartverket coastline · NVE HydAPI</span>
                            <button onClick={() => setCoolingWaterData(null)} style={{ fontSize: 10, color: '#475569', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>clear</button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}{/* end Cooling Water render (Analysis tab) */}

        </div>
      </div>
    </div>
  );
};

export default AnalysisPanel;
